import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, inject, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { FikoYT3dLine, FikoYT3dLineHandles, ICizgiAyarlari, CizgiStili, OkTipi } from './fikoyt-3d-line';
import { FikoSahne } from '../../FikoSahne';
import { FikoYTService } from '@/src/service/fikoyt.service';

/**
 * Çizgi oluşturma yöntemi
 */
export type CizgiOlusturmaYontemi =
    | 'noktadanNoktaya'
    | 'noktadanYonUzunluk'
    | 'noktadanAciUzunluk'
    | 'noktadan3DAci'
    | 'yatay'
    | 'dikey'
    | 'derinlik'
    | 'merkezdenCift';

/**
 * FikoYT 3D Line Dialog Component
 *
 * 3D çizgi oluşturmak ve ayarlamak için kullanılan dialog bileşeni.
 * Tüm çizgi özelliklerini ayarlamaya olanak tanır.
 *
 * FikoDialogService ile kullanım:
 * - pencere.parametreler.sahne: FikoSahne referansı
 * - pencere.parametreler.cizgiOlusturulduCallback: Callback fonksiyon
 */
@Component({
    selector: 'fikoyt-3d-line-dialog',
    templateUrl: './fikoyt-3d-line-dialog.component.html',
    styleUrls: ['./fikoyt-3d-line-dialog.component.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule]
})
export class FikoYT3dLineDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    /** Dialog açılış sayacı - her açılışta artırılır */
    private dialogAcilisSayaci: number = 0;

    /** Son işlenen açılış sayacı */
    private sonIslenenAcilis: number = 0;

    /** FikoDialogService pencere referansı (private backing field) */
    private _pencere?: any;

    /** Pencere getter */
    get pencere(): any {
        return this._pencere;
    }

    /** Pencere setter - wrapper nesnesinden instance'ı çıkarır */
    @Input()
    set pencere(v: any) {
        // FikoDialogService wrapper nesnesinden dialog core instance'ını çıkar
        const yeniPencere = v?.instance || v;

        console.log('[Handle Debug] pencere setter çağrıldı', {
            yeniPencere: !!yeniPencere,
            _duzenleModu: this._duzenleModu,
            _mevcutCizgi: !!this._mevcutCizgi
        });

        // NOT: "Aynı pencere tekrar set ediliyorsa çık" kontrolü KALDIRILDI
        // Çünkü dialog tekrar açıldığında handle'ların yeniden başlatılması gerekiyor

        // Eski pencere referansını sakla
        const eskiPencere = this._pencere;
        this._pencere = yeniPencere;

        // ÖNEMLİ: FikoDialogCore'da 'parametreler' property'si YOK!
        // Parametreler Angular ngComponentOutlet ile ayrı @Input() olarak geçiyor.
        // Bu yüzden burada parametreleri aramak yerine, sadece dialog açıldığını algılayıp
        // handle'ları yeniden başlatmalıyız.

        // Dialog açıldığında handle'ları başlat
        // pencere setter her açılışta çağrılır (refreshId değiştiği için)
        // NOT: İlk açılışta eskiPencere undefined olur - bu durumda ngOnInit halledecek
        // Sadece TEKRAR açılışlarda (eskiPencere var) müdahale et
        if (yeniPencere && eskiPencere) {
            // Bu bir TEKRAR açılış - cached component için state'i yenile
            if (this._duzenleModu && this._mevcutCizgi) {
                // DÜZENLEME MODU: Handle'ları yeniden başlat
                console.log('[Handle Debug] pencere setter: TEKRAR AÇILIŞ - DÜZENLEME MODU, dialogYenidenAcildi çağrılacak');
                this.dialogYenidenAcildi();
            } else if (!this._duzenleModu) {
                // YENİ ÇİZGİ MODU: Önceki geçici çizgiyi temizle ve yeni oluştur
                console.log('[Handle Debug] pencere setter: TEKRAR AÇILIŞ - YENİ ÇİZGİ MODU, yeniCizgiModuBaslat çağrılacak');
                this.yeniCizgiModuBaslat();
            }
        }
    }

    /**
     * Yeni çizgi modu başlatılırken çağrılır
     * Dialog tekrar açıldığında state'i sıfırlar ve yeni geçici çizgi oluşturur
     */
    private yeniCizgiModuBaslat(): void {
        console.log('[Yeni Çizgi] yeniCizgiModuBaslat çağrıldı');

        // 1. Önce mevcut handle'ları temizle
        this.handleDurdur();

        // 2. Eski geçici çizgiyi temizle (varsa)
        this.geciciCizgiSil();

        // 3. Form değerlerini varsayılana sıfırla
        this.formDegerleriniSifirla();

        // 4. Kısa bir gecikme sonra yeni geçici çizgi oluştur
        // (DOM'un hazır olması için)
        setTimeout(() => {
            this.geciciCizgiOlustur();
        }, 50);
    }

    /**
     * Form değerlerini varsayılan değerlere sıfırlar
     */
    private formDegerleriniSifirla(): void {
        // Koordinatları varsayılan değerlere sıfırla
        this.baslangicX = 0;
        this.baslangicY = 0;
        this.baslangicZ = 0;
        this.bitisX = 100;
        this.bitisY = 0;
        this.bitisZ = 0;

        // Diğer değerler
        this.uzunluk = 100;
        this.thetaDerece = 0;
        this.phiDerece = 90;

        // İsim sıfırla
        this.isim = '';

        // Yöntem varsayılana
        this.seciliYontem = 'noktadanNoktaya';

        // Stil varsayılanları
        this.renk = '#ffffff';
        this.kalinlik = 2;
        this.opaklik = 1;
        this.stil = 'duz';
        this.cizgiTipi = 'Line';

        // Seçenekler
        this.enUstte = false;
        this.renderEt = true;
        this.secilebilir = true;
        this.katman = 0;

        console.log('[Yeni Çizgi] Form değerleri sıfırlandı');
    }

    /** Ana gösterim referansı (FikoThreeJS) */
    @Input() anaGosterim?: any;

    /** Sahne referansı */
    @Input() sahne?: FikoSahne;

    /** Aktif Body (oluşturulan çizgiler bu body'ye eklenir) */
    @Input() aktifBody?: any;

    /** Düzenleme modunda mı? (mevcut çizgi düzenleniyor) */
    private _duzenleModu: boolean = false;
    @Input()
    get duzenleModu(): boolean { return this._duzenleModu; }
    set duzenleModu(v: boolean) {
        console.log('[Handle Debug] duzenleModu setter', { yeniDeger: v, eskiDeger: this._duzenleModu, mevcutCizgi: !!this._mevcutCizgi });
        const eskiDeger = this._duzenleModu;
        this._duzenleModu = v;
        // Düzenleme modu açıldığında handle'ları başlat
        if (v && !eskiDeger && this._mevcutCizgi) {
            console.log('[Handle Debug] duzenleModu: koşullar sağlandı, handle başlatılacak');
            this.mevcutCizgiyiYukle();
            this.handleDurdur();
            this.handleBaslat();
        }
    }

    /** Düzenlenecek çizgi (duzenleModu=true ise) */
    private _mevcutCizgi?: FikoYT3dLine;
    @Input()
    get mevcutCizgi(): FikoYT3dLine | undefined { return this._mevcutCizgi; }
    set mevcutCizgi(v: FikoYT3dLine | undefined) {
        console.log('[Handle Debug] mevcutCizgi setter', { yeniCizgi: !!v, eskiCizgi: !!this._mevcutCizgi, ayniMi: this._mevcutCizgi === v });

        // NOT: "Aynı çizgi" kontrolü KALDIRILDI - dialog tekrar açıldığında handle'lar yeniden başlatılmalı
        // Eski kod: if (this._mevcutCizgi === v) return;

        this._mevcutCizgi = v;
        // Çizgi set edildiğinde ve düzenleme modundaysa handle'ları başlat
        if (v && this._duzenleModu) {
            console.log('[Handle Debug] mevcutCizgi: koşullar sağlandı, handle başlatılacak');
            this.mevcutCizgiyiYukle();
            this.handleDurdur();
            this.handleBaslat();
        }
    }

    /** Dialog görünür mü? (standalone kullanım için) */
    private _gorunur: boolean = true;
    @Input()
    get gorunur(): boolean { return this._gorunur; }
    set gorunur(v: boolean) {
        console.log('[Handle Debug] gorunur setter', { yeniDeger: v, eskiDeger: this._gorunur, duzenleModu: this._duzenleModu, mevcutCizgi: !!this._mevcutCizgi });
        const eskiDeger = this._gorunur;
        this._gorunur = v;

        // Dialog görünür hale geldiğinde ve düzenleme modundaysa handle'ları başlat
        if (v && !eskiDeger && this._duzenleModu && this._mevcutCizgi) {
            console.log('[Handle Debug] gorunur: koşullar sağlandı, handle başlatılacak');
            setTimeout(() => {
                this.mevcutCizgiyiYukle();
                this.handleDurdur();
                this.handleBaslat();
                this.cd.detectChanges();
            }, 100);
        }
    }

    /** Çizgi oluşturulduğunda emit */
    @Output() cizgiOlusturuldu = new EventEmitter<FikoYT3dLine>();

    /** Çizgi güncellendiğinde emit */
    @Output() cizgiGuncellendi = new EventEmitter<FikoYT3dLine>();

    /** Dialog kapatıldığında emit */
    @Output() kapandi = new EventEmitter<void>();

    /** Çizgi silindi eventi */
    @Output() cizgiSilindi = new EventEmitter<FikoYT3dLine>();

    /** Callback fonksiyon (FikoDialogService kullanımı için) */
    private cizgiOlusturulduCallback?: (cizgi: FikoYT3dLine) => void;

    private cd = inject(ChangeDetectorRef);
    private fikoYT = inject(FikoYTService);

    /** Body seçimi için subscription */
    private bodySecimSubscription?: Subscription;

    // ========================================================================
    // FORM DEĞERLERİ
    // ========================================================================

    /** Seçili oluşturma yöntemi */
    seciliYontem: CizgiOlusturmaYontemi = 'noktadanNoktaya';

    /** Çizgi ismi (boş bırakılırsa otomatik numaralandırılır) */
    isim: string = '';

    // Nokta koordinatları
    baslangicX: number = 0;
    baslangicY: number = 0;
    baslangicZ: number = 0;

    bitisX: number = 100;
    bitisY: number = 0;
    bitisZ: number = 0;

    // Yön ve uzunluk
    yonX: number = 1;
    yonY: number = 0;
    yonZ: number = 0;
    uzunluk: number = 100;

    // Açı değerleri
    aciDerece: number = 0;
    thetaDerece: number = 0;
    phiDerece: number = 90;

    // Çizgi tipi
    cizgiTipi: 'Line2' | 'Line' | 'LineSegments' = 'Line';

    /** Çizgi tipi seçenekleri */
    cizgiTipiSecenekleri = [
        { deger: 'Line', etiket: 'Line (Basit)' },
        { deger: 'Line2', etiket: 'Line2 (Kalın)' },
        { deger: 'LineSegments', etiket: 'LineSegments' }
    ];

    // Çizgi ayarları
    renk: string = '#00ff00';
    kalinlik: number = 1;
    stil: CizgiStili = 'duz';
    kesikliAralik: number = 5;
    kesikliBoyut: number = 10;
    opaklik: number = 1;

    // Ok ayarları
    okBaslangic: OkTipi = 'yok';
    okBitis: OkTipi = 'yok';
    okBoyut: number = 0.1;

    // Diğer ayarlar
    secilebilir: boolean = true;
    katman: number = 0;
    enUstte: boolean = false;
    renderEt: boolean = true;

    // Aktif Body seçimi (ürün ağacı entegrasyonu - CATIA tarzı)
    seciliAktifBody: any = null;
    aktifBodyListesi: { id: string; isim: string; nesne: any }[] = [];
    aktifBodyeEkle: boolean = true;  // Body'ye eklensin mi?
    bodySecModu: boolean = false;  // Ürün ağacından seçim modu

    /** Seçili body'nin görüntülenen ismi */
    get seciliAktifBodyIsim(): string {
        if (!this.seciliAktifBody) return '(Seçilmedi)';
        return this.seciliAktifBody.name || this.seciliAktifBody.isim || 'Body';
    }

    // Nokta seçme modu
    noktaSecModu: 'baslangic' | 'bitis' | null = null;
    private originalClickHandler?: (event: MouseEvent) => void;

    // Yön seçme modu
    yonSecModu: 'ilkNokta' | 'ikinciNokta' | null = null;
    yonSecTipi: 'yuzey' | 'kenar' | 'ikiNokta' | null = null;
    private yonIlkNokta: THREE.Vector3 | null = null;

    // ========================================================================
    // HANDLE SİSTEMİ (CATIA-Style Draggable Handles)
    // ========================================================================

    /** Handle'lar grubu */
    private handles?: FikoYT3dLineHandles;

    /** Handle sürükleme modu aktif mi */
    handleAktif: boolean = false;

    // ========================================================================
    // GEÇİCİ ÇİZGİ ÖNİZLEME SİSTEMİ
    // ========================================================================

    /** Geçici önizleme çizgisi (yeni çizgi modunda kullanılır) */
    private geciciCizgi?: FikoYT3dLine;

    /** Geçici çizgi oluşturuldu mu? */
    private geciciCizgiAktif: boolean = false;

    /** Sürükleme düzlemi (kamera-paralel) */
    private dragPlane: THREE.Plane = new THREE.Plane();

    /** Sürükleme için raycaster */
    private handleRaycaster: THREE.Raycaster = new THREE.Raycaster();

    /** Sürükleme offset'i */
    private dragOffset: THREE.Vector3 = new THREE.Vector3();

    /** Mouse event handler referansları */
    private handleMouseDownRef?: (e: MouseEvent) => void;
    private handleMouseMoveRef?: (e: MouseEvent) => void;
    private handleMouseUpRef?: (e: MouseEvent) => void;

    // ========================================================================
    // SAYFA YÖNETİMİ (Android-style navigation)
    // ========================================================================

    /** Aktif sayfa: 0=Ana, 1=Ayarlar */
    aktifSayfa: number = 0;

    /** Ayarlar sayfasında aktif tab */
    aktifAyarTab: 'gorunum' | 'ok' | 'diger' = 'gorunum';

    /** Sayfalar arası geçiş */
    sayfayaGit(sayfa: number): void {
        this.aktifSayfa = sayfa;
        this.cd.detectChanges();
    }

    /** Geri git - her zaman ana sayfaya dön */
    geriGit(): void {
        this.aktifSayfa = 0;
        this.cd.detectChanges();
    }

    /** Tab değiştir */
    tabDegistir(tab: 'gorunum' | 'ok' | 'diger'): void {
        this.aktifAyarTab = tab;
        this.cd.detectChanges();
    }

    // Yöntem seçenekleri
    yontemSecenekleri: { deger: CizgiOlusturmaYontemi; etiket: string; aciklama: string }[] = [
        { deger: 'noktadanNoktaya', etiket: 'Nokta → Nokta', aciklama: 'İki nokta arası çizgi' },
        { deger: 'noktadanYonUzunluk', etiket: 'Nokta + Yön + Uzunluk', aciklama: 'Başlangıç, yön vektörü ve uzunluk' },
        { deger: 'noktadanAciUzunluk', etiket: 'Nokta + Açı + Uzunluk', aciklama: 'XY düzleminde açı ile' },
        { deger: 'noktadan3DAci', etiket: 'Nokta + 3D Açı', aciklama: 'Küresel koordinat (theta, phi)' },
        { deger: 'yatay', etiket: 'Yatay Çizgi', aciklama: 'X ekseninde yatay' },
        { deger: 'dikey', etiket: 'Dikey Çizgi', aciklama: 'Y ekseninde dikey' },
        { deger: 'derinlik', etiket: 'Derinlik Çizgisi', aciklama: 'Z ekseninde' },
        { deger: 'merkezdenCift', etiket: 'Merkezden Çift Yönlü', aciklama: 'Merkez noktadan iki yöne' }
    ];

    // Stil seçenekleri
    stilSecenekleri: { deger: CizgiStili; etiket: string }[] = [
        { deger: 'duz', etiket: 'Düz' },
        { deger: 'kesikli', etiket: 'Kesikli' },
        { deger: 'noktali', etiket: 'Noktalı' },
        { deger: 'kesikli-noktali', etiket: 'Kesikli-Noktalı' }
    ];

    // Ok tipi seçenekleri
    okTipiSecenekleri: { deger: OkTipi; etiket: string }[] = [
        { deger: 'yok', etiket: 'Yok' },
        { deger: 'ok', etiket: 'Ok' },
        { deger: 'ucgen', etiket: 'Üçgen' },
        { deger: 'kare', etiket: 'Kare' },
        { deger: 'daire', etiket: 'Daire' }
    ];

    ngOnInit(): void {
        // FikoDialogService parametrelerini al
        if (this.pencere?.parametreler) {
            const params = this.pencere.parametreler;

            if (params.sahne) this.sahne = params.sahne;
            if (params.anaGosterim) this.anaGosterim = params.anaGosterim;
            if (params.aktifBody) this.aktifBody = params.aktifBody;
            if (params.cizgiOlusturulduCallback) {
                this.cizgiOlusturulduCallback = params.cizgiOlusturulduCallback;
            }
            // Düzenleme modu parametrelerini al
            if (params.duzenleModu !== undefined) this.duzenleModu = params.duzenleModu;
            if (params.mevcutCizgi) this.mevcutCizgi = params.mevcutCizgi;
        }

        // Aktif body listesini yükle (ürün ağacından)
        this.aktifBodyListesiniYukle();

        // Düzenleme modunda mevcut çizginin değerlerini yükle
        if (this.duzenleModu && this.mevcutCizgi) {
            this.mevcutCizgiyiYukle();
            // Handle sistemini başlat (önceki varsa temizle)
            this.handleDurdur();
            this.handleBaslat();
        } else {
            // YENİ ÇİZGİ MODU: Geçici çizgi oluştur ve sahneye ekle
            this.geciciCizgiOlustur();
        }
    }

    ngAfterViewInit(): void {
        // View hazır
    }

    ngOnDestroy(): void {
        // Handle sistemini durdur
        this.handleDurdur();

        // Geçici çizgiyi temizle (iptal durumu için)
        // NOT: Kaydet tıklandığında geciciCizgi undefined yapılır, bu yüzden sadece iptal durumunda silinir
        this.geciciCizgiSil();

        // Nokta, yön ve body seçme modlarını temizle
        this.noktaSecDurdur();
        this.yonSecDurdur();
        this.bodySecDurdur();
    }

    // ========================================================================
    // GEÇİCİ ÇİZGİ YÖNETİMİ
    // ========================================================================

    /**
     * Geçici önizleme çizgisi oluştur
     * Dialog açıldığında hemen sahneye eklenir, kullanıcı değişiklikleri anında görür
     */
    private geciciCizgiOlustur(): void {
        // Sahne kontrolü
        const sahneObj = this.anaGosterim?.sahneler?.[0];
        const hedefSahne = sahneObj || this.sahne;
        if (!hedefSahne) return;

        // Geçici çizgi ayarları
        const ayarlar = this.ayarlariOlustur();

        // Form değerlerinden başlangıç ve bitiş noktalarını hesapla
        const { baslangic, bitis } = this.noktalarHesapla();

        // Geçici çizgi oluştur (sahne parametresi undefined - biz kendimiz ekleyeceğiz)
        this.geciciCizgi = FikoYT3dLine.noktadanNoktaya(
            baslangic,
            bitis,
            ayarlar,
            undefined, // Sahne parametresi undefined, böylece çift ekleme olmaz
            '(Önizleme) ' + (this.isim || 'Yeni Çizgi')
        );

        // Geçici çizgiyi işaretle
        this.geciciCizgi.userData['geciciOnizleme'] = true;

        // Sahneye tek bir kez ekle
        hedefSahne.add(this.geciciCizgi);

        this.geciciCizgiAktif = true;

        // Handle sistemini başlat (geçici çizgi için de ok handle'ları göster)
        this.handleBaslatGecici();

        // Render'ı tetikle
        if (this.anaGosterim?.sunucu && this.anaGosterim?.aktifKamera) {
            this.anaGosterim.sunucu.render(hedefSahne, this.anaGosterim.aktifKamera);
        }
    }

    /** Geçici handle retry sayacı */
    private handleGeciciRetryCount: number = 0;

    /**
     * Geçici çizgi için handle sistemini başlat
     */
    private handleBaslatGecici(): void {
        if (!this.geciciCizgi) {
            console.warn('[Handle Geçici] geciciCizgi yok');
            return;
        }

        // Önceki handle'ları temizle
        this.handleDurdur();

        // Sahneyi al
        const sahneObj = this.anaGosterim?.sahneler?.[0];
        const hedefSahne = sahneObj || this.sahne;
        if (!hedefSahne) {
            // Sahne henüz hazır değil, retry yap
            if (this.handleGeciciRetryCount < this.HANDLE_MAX_RETRY) {
                this.handleGeciciRetryCount++;
                console.warn(`[Handle Geçici] Sahne hazır değil, retry ${this.handleGeciciRetryCount}/${this.HANDLE_MAX_RETRY}`);
                this.handleGeciciTimeoutId = setTimeout(() => this.handleBaslatGecici(), this.HANDLE_RETRY_DELAY);
            } else {
                console.error('[Handle Geçici] Sahne bulunamadı, maksimum retry aşıldı');
                this.handleGeciciRetryCount = 0;
            }
            return;
        }

        // Canvas'ı kontrol et
        const canvas = this.canvasBul();
        if (!canvas) {
            // Canvas henüz hazır değil, retry yap
            if (this.handleGeciciRetryCount < this.HANDLE_MAX_RETRY) {
                this.handleGeciciRetryCount++;
                console.warn(`[Handle Geçici] Canvas hazır değil, retry ${this.handleGeciciRetryCount}/${this.HANDLE_MAX_RETRY}`);
                this.handleGeciciTimeoutId = setTimeout(() => this.handleBaslatGecici(), this.HANDLE_RETRY_DELAY);
            } else {
                console.error('[Handle Geçici] Canvas bulunamadı, maksimum retry aşıldı');
                this.handleGeciciRetryCount = 0;
            }
            return;
        }

        // Retry başarılı, sayacı sıfırla
        this.handleGeciciRetryCount = 0;

        // Yeni handle'lar oluştur
        this.handles = new FikoYT3dLineHandles(this.geciciCizgi);

        // Sahneye ekle
        hedefSahne.add(this.handles);

        // Handle'ın görünür olduğundan emin ol
        this.handles.visible = true;
        this.handles.traverse((child: THREE.Object3D) => {
            child.visible = true;
            child.frustumCulled = false;
        });
        this.handles.guncelle();

        // Handle değişiklik callback'i
        this.handles.onChange = (baslangic: THREE.Vector3, bitis: THREE.Vector3) => {
            // Form değerlerini güncelle
            this.baslangicX = Math.round(baslangic.x * 100) / 100;
            this.baslangicY = Math.round(baslangic.y * 100) / 100;
            this.baslangicZ = Math.round(baslangic.z * 100) / 100;
            this.bitisX = Math.round(bitis.x * 100) / 100;
            this.bitisY = Math.round(bitis.y * 100) / 100;
            this.bitisZ = Math.round(bitis.z * 100) / 100;

            // Uzunluğu güncelle
            this.uzunluk = Math.round(baslangic.distanceTo(bitis) * 100) / 100;

            this.cd.detectChanges();
        };

        this.handleAktif = true;

        // Event handler referanslarını oluştur
        this.handleMouseDownRef = (e: MouseEvent) => this.handleMouseDown(e);
        this.handleMouseMoveRef = (e: MouseEvent) => this.handleMouseMove(e);
        this.handleMouseUpRef = (e: MouseEvent) => this.handleMouseUp(e);

        // Event listener'ları ekle
        canvas.addEventListener('mousedown', this.handleMouseDownRef);
        canvas.addEventListener('mousemove', this.handleMouseMoveRef);
        canvas.addEventListener('mouseup', this.handleMouseUpRef);

        // Render'ı tetikle (handle'ların görünmesi için)
        if (this.anaGosterim?.sunucu && this.anaGosterim?.aktifKamera) {
            this.anaGosterim.sunucu.render(hedefSahne, this.anaGosterim.aktifKamera);
        }

        console.log('[Handle Geçici] Handle\'lar başarıyla başlatıldı');
    }

    /**
     * Geçici çizgiyi güncelle (form değerleri değiştiğinde)
     */
    private geciciCizgiGuncelle(): void {
        if (!this.geciciCizgi || !this.geciciCizgiAktif) return;

        // Noktaları hesapla
        const { baslangic, bitis } = this.noktalarHesapla();

        // Çizgi pozisyonlarını güncelle
        this.geciciCizgi.baslangic = baslangic;
        this.geciciCizgi.bitis = bitis;

        // Ayarları güncelle
        this.geciciCizgi.renk = this.renk;
        this.geciciCizgi.kalinlik = this.kalinlik;
        this.geciciCizgi.stil = this.stil;
        this.geciciCizgi.opaklık = this.opaklik;
        this.geciciCizgi.cizgiTipi = this.cizgiTipi;

        // İsmi güncelle
        this.geciciCizgi.name = '(Önizleme) ' + (this.isim || 'Yeni Çizgi');

        // Handle'ları güncelle (pozisyonlar değiştiğinde)
        if (this.handles) {
            this.handles.guncelle();
        }
    }

    /**
     * Geçici çizgiyi sil (iptal veya destroy durumunda)
     */
    private geciciCizgiSil(): void {
        if (!this.geciciCizgi) return;

        // Referansı sakla
        const cizgi = this.geciciCizgi;

        // Önce görünürlüğü kapat (anında görünmez olsun)
        cizgi.visible = false;

        // Sahneyi bul
        const sahneObj = this.anaGosterim?.sahneler?.[0];

        // Tüm olası parent'lardan kaldır
        if (cizgi.parent) {
            cizgi.parent.remove(cizgi);
        }
        // Her durumda sahnelerden de kaldırmayı dene (güvenlik için)
        if (sahneObj) {
            sahneObj.remove(cizgi);
        }
        if (this.sahne && this.sahne !== sahneObj) {
            this.sahne.remove(cizgi);
        }

        // Kaynakları temizle (geometry, material vb.)
        try {
            cizgi.temizle();
        } catch (e) {
            // Temizleme hatası olsa bile devam et
        }

        // Referansları temizle
        this.geciciCizgi = undefined;
        this.geciciCizgiAktif = false;

        // Render'ı tetikle
        const renderSahne = sahneObj || this.sahne;
        if (this.anaGosterim?.sunucu && this.anaGosterim?.aktifKamera && renderSahne) {
            this.anaGosterim.sunucu.render(renderSahne, this.anaGosterim.aktifKamera);
        }
    }

    /**
     * Geçici çizgiyi kalıcı yap (Kaydet tıklandığında)
     * Mevcut geçici çizgiyi sahip değişikliğiyle kalıcı hale getirir
     */
    private geciciCizgiyiKaliciYap(): FikoYT3dLine | null {
        if (!this.geciciCizgi) return null;

        // Geçici işaretini kaldır
        delete this.geciciCizgi.userData['geciciOnizleme'];

        // İsmi düzelt (önizleme prefix'ini kaldır)
        this.geciciCizgi.name = this.isim || ('Çizgi.' + FikoYT3dLine.SAYAC++);

        // Referansı sakla ve temizle (silme işlemi yapılmasın)
        const kaliciCizgi = this.geciciCizgi;
        this.geciciCizgi = undefined;
        this.geciciCizgiAktif = false;

        return kaliciCizgi;
    }

    /**
     * Form değerlerinden başlangıç ve bitiş noktalarını hesapla
     */
    private noktalarHesapla(): { baslangic: THREE.Vector3; bitis: THREE.Vector3 } {
        let baslangic: THREE.Vector3;
        let bitis: THREE.Vector3;

        switch (this.seciliYontem) {
            case 'noktadanNoktaya':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.bitisY, this.bitisZ);
                break;

            case 'noktadanYonUzunluk':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const yon = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                bitis = baslangic.clone().add(yon.multiplyScalar(this.uzunluk));
                break;

            case 'noktadanAciUzunluk':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const aciRad = this.aciDerece * Math.PI / 180;
                bitis = new THREE.Vector3(
                    baslangic.x + Math.cos(aciRad) * this.uzunluk,
                    baslangic.y + Math.sin(aciRad) * this.uzunluk,
                    baslangic.z
                );
                break;

            case 'noktadan3DAci':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const theta = this.thetaDerece * Math.PI / 180;
                const phi = this.phiDerece * Math.PI / 180;
                bitis = new THREE.Vector3(
                    baslangic.x + this.uzunluk * Math.sin(phi) * Math.cos(theta),
                    baslangic.y + this.uzunluk * Math.cos(phi),
                    baslangic.z + this.uzunluk * Math.sin(phi) * Math.sin(theta)
                );
                break;

            case 'yatay':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.baslangicY, this.baslangicZ);
                break;

            case 'dikey':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.baslangicX, this.bitisY, this.baslangicZ);
                break;

            case 'derinlik':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.baslangicX, this.baslangicY, this.bitisZ);
                break;

            case 'merkezdenCift':
                const merkez = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const yonCift = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                const yarimUzunluk = this.uzunluk / 2;
                baslangic = merkez.clone().sub(yonCift.clone().multiplyScalar(yarimUzunluk));
                bitis = merkez.clone().add(yonCift.clone().multiplyScalar(yarimUzunluk));
                break;

            default:
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.bitisY, this.bitisZ);
        }

        return { baslangic, bitis };
    }

    /**
     * Dialog yeniden açıldığında çağrılacak public metod
     * FikoDialogService tarafından dialog tekrar gösterildiğinde tetiklenir
     */
    dialogYenidenAcildi(): void {
        console.log('[Handle Debug] dialogYenidenAcildi çağrıldı', {
            duzenleModu: this.duzenleModu,
            mevcutCizgi: !!this.mevcutCizgi,
            pencere: !!this._pencere
        });

        // NOT: Sayaç kontrolü kaldırıldı - her çağrıda handle'ları yeniden başlat
        // Eski handle'lar handleDurdur() ile temizlenecek
        if (this.duzenleModu && this.mevcutCizgi) {
            console.log('[Handle Debug] Koşullar sağlandı, handle başlatılacak');
            this.mevcutCizgiyiYukle();
            this.handleDurdur();
            // Timeout ID sakla - sonraki çağrıda iptal edilebilmesi için
            this.handleBaslatTimeoutId = setTimeout(() => {
                console.log('[Handle Debug] handleBaslat timeout çalıştı');
                this.handleBaslat();
                this.cd.detectChanges();
            }, 100); // 50ms'den 100ms'ye çıkarıldı - DOM'un hazır olması için
        } else {
            console.warn('[Handle Debug] Koşullar sağlanmadı!');
        }
    }

    // ========================================================================
    // NOKTA SEÇME (3D'den)
    // ========================================================================

    /**
     * Canvas elementini bul (FikoSecililer pattern)
     */
    private canvasBul(): HTMLCanvasElement | null {
        // FikoSecililer pattern: anaGosterim.canvas.nativeElement
        if (this.anaGosterim?.canvas?.nativeElement) {
            return this.anaGosterim.canvas.nativeElement as HTMLCanvasElement;
        }

        // Alternatif: sunucu.domElement
        if (this.anaGosterim?.sunucu?.domElement) {
            return this.anaGosterim.sunucu.domElement as HTMLCanvasElement;
        }

        // sahne üzerinden dene
        if (this.sahne) {
            const anaGosterim = (this.sahne as any).anaGosterim;
            if (anaGosterim?.canvas?.nativeElement) {
                return anaGosterim.canvas.nativeElement as HTMLCanvasElement;
            }
            if (anaGosterim?.sunucu?.domElement) {
                return anaGosterim.sunucu.domElement as HTMLCanvasElement;
            }
        }

        // DOM'dan direkt arama yap (fallback)
        const canvasElement = document.querySelector('canvas#fikoThreeCanvas') ||
                             document.querySelector('canvas.fiko-threejs-canvas') ||
                             document.querySelector('.fiko-three-js-container canvas');
        if (canvasElement) return canvasElement as HTMLCanvasElement;

        return null;
    }

    /**
     * Nokta seçme modunu başlat
     */
    noktaSecBaslat(mod: 'baslangic' | 'bitis'): void {
        // Aynı moda tıklandıysa kapat
        if (this.noktaSecModu === mod) {
            this.noktaSecDurdur();
            return;
        }

        // Yön seçme modunu kapat
        if (this.yonSecModu !== null) {
            this.yonSecDurdur();
        }

        this.noktaSecModu = mod;

        // Canvas'ı bul
        const canvas = this.canvasBul();
        if (!canvas) {
            console.warn('[3D Çizgi Dialog] Canvas bulunamadı. anaGosterim:', this.anaGosterim);
            this.noktaSecModu = null;
            return;
        }

        // Önceki handler'ı temizle
        if (this.originalClickHandler) {
            canvas.removeEventListener('click', this.originalClickHandler);
        }

        // Yeni click handler
        this.originalClickHandler = (event: MouseEvent) => {
            this.canvasTiklandi(event, canvas);
        };

        canvas.addEventListener('click', this.originalClickHandler);
        this.cd.detectChanges();
    }

    /**
     * Nokta seçme modunu durdur
     */
    noktaSecDurdur(): void {
        this.noktaSecModu = null;

        // Click handler'ı kaldır
        const canvas = this.canvasBul();
        if (canvas && this.originalClickHandler) {
            canvas.removeEventListener('click', this.originalClickHandler);
            this.originalClickHandler = undefined;
        }

        this.cd.detectChanges();
    }

    /**
     * Canvas tıklandığında 3D koordinat hesapla
     */
    private canvasTiklandi(event: MouseEvent, canvas: HTMLCanvasElement): void {
        if (!this.noktaSecModu) return;

        // Ortak raycast metodunu kullan
        const hitPoint = this.raycastNoktaHesapla(event, canvas);
        if (!hitPoint) return;

        // Koordinatları yuvarlayarak forma aktar
        const roundedX = Math.round(hitPoint.x * 100) / 100;
        const roundedY = Math.round(hitPoint.y * 100) / 100;
        const roundedZ = Math.round(hitPoint.z * 100) / 100;

        if (this.noktaSecModu === 'baslangic') {
            this.baslangicX = roundedX;
            this.baslangicY = roundedY;
            this.baslangicZ = roundedZ;
        } else if (this.noktaSecModu === 'bitis') {
            this.bitisX = roundedX;
            this.bitisY = roundedY;
            this.bitisZ = roundedZ;
        }

        // Seçme modunu kapat
        this.noktaSecDurdur();

        // Çizgiyi hemen güncelle
        this.cizgiyiAnlikGuncelle();
    }

    /**
     * Hızlı yön seçimi (+X, -X, +Y, -Y, +Z, -Z)
     */
    hizliYonSec(x: number, y: number, z: number): void {
        this.yonX = x;
        this.yonY = y;
        this.yonZ = z;

        // Çizgiyi hemen güncelle
        this.cizgiyiAnlikGuncelle();

        this.cd.detectChanges();
    }

    /**
     * Yön seçme modunu başlat
     * @param tip Seçim tipi: 'yuzey' (yüzey normali), 'kenar' (kenar yönü), 'ikiNokta' (2 nokta)
     */
    yonSecBaslat(tip: 'yuzey' | 'kenar' | 'ikiNokta'): void {
        // Aynı tipe tıklandıysa kapat
        if (this.yonSecTipi === tip && this.yonSecModu !== null) {
            this.yonSecDurdur();
            return;
        }

        // Nokta seçme modunu kapat
        if (this.noktaSecModu !== null) {
            this.noktaSecDurdur();
        }

        this.yonSecTipi = tip;
        this.yonIlkNokta = null;

        // Seçim tipine göre mod ayarla
        if (tip === 'ikiNokta') {
            this.yonSecModu = 'ilkNokta';
        } else {
            this.yonSecModu = 'ilkNokta'; // tek tıkla seçim için de kullanılır
        }

        // Canvas'ı bul ve click event ekle
        const canvas = this.canvasBul();
        if (!canvas) {
            console.warn('[3D Çizgi Dialog] Canvas bulunamadı');
            this.yonSecModu = null;
            this.yonSecTipi = null;
            return;
        }

        // Önceki handler'ı temizle
        if (this.originalClickHandler) {
            canvas.removeEventListener('click', this.originalClickHandler);
        }

        // Yeni click handler - seçim tipine göre
        this.originalClickHandler = (event: MouseEvent) => {
            switch (this.yonSecTipi) {
                case 'yuzey':
                    this.canvasTiklandiYuzey(event, canvas);
                    break;
                case 'kenar':
                    this.canvasTiklandiKenar(event, canvas);
                    break;
                case 'ikiNokta':
                    this.canvasTiklandiYon(event, canvas);
                    break;
            }
        };

        canvas.addEventListener('click', this.originalClickHandler);
        this.cd.detectChanges();
    }

    /**
     * Yön seçme modunu durdur
     */
    yonSecDurdur(): void {
        this.yonSecModu = null;
        this.yonSecTipi = null;
        this.yonIlkNokta = null;

        // Click handler'ı kaldır
        const canvas = this.canvasBul();
        if (canvas && this.originalClickHandler) {
            canvas.removeEventListener('click', this.originalClickHandler);
            this.originalClickHandler = undefined;
        }

        this.cd.detectChanges();
    }

    // ========================================================================
    // BODY SEÇME MODU (CATIA tarzı ürün ağacından seçim)
    // ========================================================================

    /**
     * Body seçme modunu başlat
     * Ürün ağacında veya 3D viewport'ta tıklanan öğeyi aktif body olarak ayarlar
     */
    bodySecBaslat(): void {
        // Zaten seçim modundaysa kapat
        if (this.bodySecModu) {
            this.bodySecDurdur();
            return;
        }

        // Diğer seçim modlarını kapat
        if (this.noktaSecModu !== null) {
            this.noktaSecDurdur();
        }
        if (this.yonSecModu !== null) {
            this.yonSecDurdur();
        }

        this.bodySecModu = true;

        // FikoYT.degisti event'ini dinle - ürün ağacından seçim yapıldığında tetiklenir
        this.bodySecimSubscription = this.fikoYT.degisti.subscribe(() => {
            if (this.bodySecModu) {
                // aktifItem seçilen öğeyi içerir
                const aktifItem = this.anaGosterim?.aktifItem;
                if (aktifItem) {
                    // Seçilen öğeyi veya parent'ını body olarak ayarla
                    const body = this.uygunBodyBul(aktifItem);
                    if (body) {
                        this.seciliAktifBody = body;
                    } else {
                        this.seciliAktifBody = aktifItem;
                    }
                    this.bodySecDurdur();
                }
            }
        });

        this.cd.detectChanges();
    }

    /**
     * Body seçme modunu durdur
     */
    bodySecDurdur(): void {
        this.bodySecModu = false;

        // Subscription'ı temizle
        if (this.bodySecimSubscription) {
            this.bodySecimSubscription.unsubscribe();
            this.bodySecimSubscription = undefined;
        }

        this.cd.detectChanges();
    }

    /**
     * Seçilen öğeden uygun body'yi bul (parent zincirinde yukarı çık)
     */
    private uygunBodyBul(nesne: any): any {
        if (!nesne) return null;

        // Bu nesne bir body/container mi kontrol et
        const isBody = nesne.tipi === 'Body' ||
                       nesne.tipi === 'Part' ||
                       nesne.tipi === 'Assembly' ||
                       nesne.type === 'FikoGrupCizim' ||
                       nesne.type === 'FikoCizimler' ||
                       nesne.type === 'FikoGrup' ||
                       nesne.type === 'Group' ||
                       (nesne.name && (nesne.name.toLowerCase().includes('body') ||
                                       nesne.name.toLowerCase().includes('part') ||
                                       nesne.name.toLowerCase().includes('çizim')));

        if (isBody) {
            return nesne;
        }

        // Parent'a bak
        if (nesne.parent && nesne.parent.type !== 'Scene') {
            return this.uygunBodyBul(nesne.parent);
        }

        // Hiç body bulunamazsa nesnenin kendisini döndür
        return nesne;
    }

    /**
     * Yön seçimi için canvas tıklaması (2 nokta ile)
     */
    private canvasTiklandiYon(event: MouseEvent, canvas: HTMLCanvasElement): void {
        if (!this.yonSecModu) return;

        // Tıklanan noktayı hesapla
        const hitPoint = this.raycastNoktaHesapla(event, canvas);
        if (!hitPoint) return;

        if (this.yonSecModu === 'ilkNokta') {
            // İlk nokta seçildi
            this.yonIlkNokta = hitPoint.clone();
            this.yonSecModu = 'ikinciNokta';
            this.cd.detectChanges();
        } else if (this.yonSecModu === 'ikinciNokta' && this.yonIlkNokta) {
            // İkinci nokta seçildi - yönü hesapla
            const direction = new THREE.Vector3().subVectors(hitPoint, this.yonIlkNokta).normalize();

            this.yonX = Math.round(direction.x * 1000) / 1000;
            this.yonY = Math.round(direction.y * 1000) / 1000;
            this.yonZ = Math.round(direction.z * 1000) / 1000;

            // Uzunluğu da hesapla (opsiyonel)
            const distance = hitPoint.distanceTo(this.yonIlkNokta);
            if (distance > 0.1) {
                this.uzunluk = Math.round(distance * 100) / 100;
            }

            // Seçme modunu kapat
            this.yonSecDurdur();

            // Çizgiyi hemen güncelle
            this.cizgiyiAnlikGuncelle();
        }
    }

    /**
     * Yüzey normalinden yön al
     */
    private canvasTiklandiYuzey(event: MouseEvent, canvas: HTMLCanvasElement): void {
        if (!this.yonSecModu) return;

        // FikoSecililer pattern: offsetX/offsetY kullan
        const x = (event.offsetX / canvas.clientWidth) * 2 - 1;
        const y = -(event.offsetY / canvas.clientHeight) * 2 + 1;

        const secililer = this.anaGosterim?.secililer;
        const camera = this.anaGosterim?.aktifKamera;
        const sahneObj = this.anaGosterim?.sahneler?.[0];

        if (!camera || !sahneObj) {
            console.warn('[3D Çizgi Dialog] Kamera veya sahne bulunamadı');
            return;
        }

        const raycaster = secililer?.raycaster || new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

        // Sadece mesh'leri (yüzeyleri) hedefle
        const intersects = raycaster.intersectObjects(sahneObj.children, true);

        for (const intersect of intersects) {
            // Face normal varsa kullan
            if (intersect.face && intersect.face.normal) {
                // Local normal'i world space'e çevir
                const normalMatrix = new THREE.Matrix3().getNormalMatrix(intersect.object.matrixWorld);
                const worldNormal = intersect.face.normal.clone().applyMatrix3(normalMatrix).normalize();

                this.yonX = Math.round(worldNormal.x * 1000) / 1000;
                this.yonY = Math.round(worldNormal.y * 1000) / 1000;
                this.yonZ = Math.round(worldNormal.z * 1000) / 1000;

                // Seçme modunu kapat
                this.yonSecDurdur();

                // Çizgiyi hemen güncelle
                this.cizgiyiAnlikGuncelle();
                return;
            }
        }
    }

    /**
     * Kenar/çizgi yönünden yön al
     */
    private canvasTiklandiKenar(event: MouseEvent, canvas: HTMLCanvasElement): void {
        if (!this.yonSecModu) return;

        // FikoSecililer pattern: offsetX/offsetY kullan
        const x = (event.offsetX / canvas.clientWidth) * 2 - 1;
        const y = -(event.offsetY / canvas.clientHeight) * 2 + 1;

        const secililer = this.anaGosterim?.secililer;
        const camera = this.anaGosterim?.aktifKamera;
        const sahneObj = this.anaGosterim?.sahneler?.[0];

        if (!camera || !sahneObj) return;

        const raycaster = secililer?.raycaster || new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

        // Tüm objeleri tara - LineSegments, Line2 veya edge'leri ara
        const intersects = raycaster.intersectObjects(sahneObj.children, true);

        for (const intersect of intersects) {
            const obj = intersect.object;

            // Line veya LineSegments ise
            if (obj.type === 'Line' || obj.type === 'LineSegments' || obj.type === 'Line2') {
                const geometry = (obj as THREE.Line).geometry;
                const posAttr = geometry.getAttribute('position');

                if (posAttr && posAttr.count >= 2) {
                    // İlk iki noktadan yön hesapla
                    const p1 = new THREE.Vector3().fromBufferAttribute(posAttr, 0);
                    const p2 = new THREE.Vector3().fromBufferAttribute(posAttr, 1);

                    // World space'e çevir
                    p1.applyMatrix4(obj.matrixWorld);
                    p2.applyMatrix4(obj.matrixWorld);

                    const direction = new THREE.Vector3().subVectors(p2, p1).normalize();

                    this.yonX = Math.round(direction.x * 1000) / 1000;
                    this.yonY = Math.round(direction.y * 1000) / 1000;
                    this.yonZ = Math.round(direction.z * 1000) / 1000;

                    // Seçme modunu kapat
                    this.yonSecDurdur();

                    // Çizgiyi hemen güncelle
                    this.cizgiyiAnlikGuncelle();
                    return;
                }
            }

            // Mesh ise, en yakın kenarı bul
            if (obj.type === 'Mesh' && intersect.face) {
                const geometry = (obj as THREE.Mesh).geometry;
                const posAttr = geometry.getAttribute('position');
                const face = intersect.face;

                if (posAttr) {
                    // Üçgenin kenarlarını al
                    const a = new THREE.Vector3().fromBufferAttribute(posAttr, face.a);
                    const b = new THREE.Vector3().fromBufferAttribute(posAttr, face.b);
                    const c = new THREE.Vector3().fromBufferAttribute(posAttr, face.c);

                    // World space'e çevir
                    a.applyMatrix4(obj.matrixWorld);
                    b.applyMatrix4(obj.matrixWorld);
                    c.applyMatrix4(obj.matrixWorld);

                    // Tıklanan noktaya en yakın kenarı bul
                    const hitPoint = intersect.point;
                    const edges = [
                        { p1: a, p2: b },
                        { p1: b, p2: c },
                        { p1: c, p2: a }
                    ];

                    let minDist = Infinity;
                    let closestEdge = edges[0];

                    for (const edge of edges) {
                        const midPoint = new THREE.Vector3().addVectors(edge.p1, edge.p2).multiplyScalar(0.5);
                        const dist = hitPoint.distanceTo(midPoint);
                        if (dist < minDist) {
                            minDist = dist;
                            closestEdge = edge;
                        }
                    }

                    // En yakın kenarın yönünü al
                    const direction = new THREE.Vector3().subVectors(closestEdge.p2, closestEdge.p1).normalize();

                    this.yonX = Math.round(direction.x * 1000) / 1000;
                    this.yonY = Math.round(direction.y * 1000) / 1000;
                    this.yonZ = Math.round(direction.z * 1000) / 1000;

                    // Seçme modunu kapat
                    this.yonSecDurdur();

                    // Çizgiyi hemen güncelle
                    this.cizgiyiAnlikGuncelle();
                    return;
                }
            }
        }
    }

    /**
     * Raycast ile nokta hesapla (ortak metod)
     */
    private raycastNoktaHesapla(event: MouseEvent, canvas: HTMLCanvasElement): THREE.Vector3 | null {
        // FikoSecililer pattern: offsetX/offsetY kullan
        const x = (event.offsetX / canvas.clientWidth) * 2 - 1;
        const y = -(event.offsetY / canvas.clientHeight) * 2 + 1;

        // FikoSecililer'den raycaster ve diğer bileşenleri al
        const secililer = this.anaGosterim?.secililer;
        const camera = this.anaGosterim?.aktifKamera;
        const sahneObj = this.anaGosterim?.sahneler?.[0];

        if (!camera || !sahneObj) return null;

        // Mevcut raycaster'ı kullan veya yeni oluştur
        const raycaster = secililer?.raycaster || new THREE.Raycaster();
        raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

        // Sahnedeki mesh'lere raycast
        const intersects = raycaster.intersectObjects(sahneObj.children, true);

        if (intersects.length > 0) {
            return intersects[0].point.clone();
        } else {
            // XZ düzleminde bir nokta hesapla
            const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
                new THREE.Vector3(0, 1, 0),
                new THREE.Vector3(0, 0, 0)
            );
            const hitPoint = new THREE.Vector3();
            raycaster.ray.intersectPlane(plane, hitPoint);
            return hitPoint || null;
        }
    }

    /**
     * Ürün ağacından mevcut body'leri yükle
     */
    private aktifBodyListesiniYukle(): void {
        this.aktifBodyListesi = [];

        // anaGosterim üzerinden ürün ağacına eriş
        const urunAgaci = this.anaGosterim?.sahneler?.[0]?.urunAgaci;
        if (!urunAgaci) {
            // Ürün ağacı yoksa, sahneyi kullan
            if (this.sahne) {
                this.aktifBodyListesi.push({
                    id: 'sahne',
                    isim: 'Ana Sahne',
                    nesne: this.sahne
                });
            }
            return;
        }

        // Ürün ağacında body'leri bul (recursive)
        this.bodyleriBul(urunAgaci);

        // Varsayılan olarak ilk body'yi seç
        if (this.aktifBodyListesi.length > 0) {
            this.seciliAktifBody = this.aktifBodyListesi[0].nesne;
        }
    }

    /**
     * Ürün ağacında recursive olarak body'leri bul
     */
    private bodyleriBul(grup: any, derinlik: number = 0): void {
        if (!grup) return;

        // Bu grubun kendisi bir body mi?
        const isBody = grup.tipi === 'Body' || grup.tipi === 'Part' || grup.tipi === 'Assembly' ||
                       grup.name?.toLowerCase().includes('body') ||
                       grup.isim?.toLowerCase().includes('body');

        if (isBody || derinlik === 0) {
            const indent = '  '.repeat(derinlik);
            this.aktifBodyListesi.push({
                id: grup.id || grup.uuid || String(this.aktifBodyListesi.length),
                isim: indent + (grup.isim || grup.name || `Body ${this.aktifBodyListesi.length + 1}`),
                nesne: grup
            });
        }

        // Alt grupları tara
        const altlar = grup.children || grup.altlar || grup.icerik || [];
        for (const alt of altlar) {
            this.bodyleriBul(alt, derinlik + 1);
        }
    }

    /**
     * Mevcut çizginin değerlerini forma yükle
     */
    private mevcutCizgiyiYukle(): void {
        if (!this.mevcutCizgi) return;

        this.isim = this.mevcutCizgi.name;

        // Nokta koordinatları
        const baslangic = this.mevcutCizgi.baslangic;
        const bitis = this.mevcutCizgi.bitis;

        this.baslangicX = baslangic.x;
        this.baslangicY = baslangic.y;
        this.baslangicZ = baslangic.z;

        this.bitisX = bitis.x;
        this.bitisY = bitis.y;
        this.bitisZ = bitis.z;

        // Yön ve uzunluk (noktadanYonUzunluk yöntemi için)
        const yon = this.mevcutCizgi.yon;
        this.yonX = yon.x;
        this.yonY = yon.y;
        this.yonZ = yon.z;
        this.uzunluk = this.mevcutCizgi.uzunluk;

        // Çizgi tipi
        this.cizgiTipi = this.mevcutCizgi.cizgiTipi;

        // Yöntem
        this.seciliYontem = this.mevcutCizgi.yontem || 'noktadanNoktaya';

        // Temel ayarlar
        this.renk = '#' + new THREE.Color(this.mevcutCizgi.renk).getHexString();
        this.kalinlik = this.mevcutCizgi.kalinlik;
        this.stil = this.mevcutCizgi.stil;
        this.opaklik = this.mevcutCizgi.opaklık;

        // Kesikli çizgi ayarları
        this.kesikliAralik = this.mevcutCizgi.kesikliAralik;
        this.kesikliBoyut = this.mevcutCizgi.kesikliBoyut;

        // Ok ayarları
        this.okBaslangic = this.mevcutCizgi.okBaslangic;
        this.okBitis = this.mevcutCizgi.okBitis;
        this.okBoyut = this.mevcutCizgi.okBoyut;

        // Diğer ayarlar
        this.secilebilir = this.mevcutCizgi.secilebilir;
        this.katman = this.mevcutCizgi.katman;
        this.enUstte = this.mevcutCizgi.enUstte;
        this.renderEt = this.mevcutCizgi.renderEt;
    }

    /**
     * Dialog'u aç
     */
    ac(): void {
        this.gorunur = true;
        this.cd.detectChanges();
    }

    /**
     * Dialog'u kapat
     */
    kapat(): void {
        // Handle sistemini temizle
        this.handleDurdur();

        // Geçici çizgiyi sil (iptal durumu)
        this.geciciCizgiSil();

        this.gorunur = false;
        this.kapandi.emit();
        this.cd.detectChanges();
    }

    /**
     * Dialog'u kapat (FikoDialogService veya standalone)
     */
    dialogKapat(): void {
        // Handle sistemini temizle (her durumda)
        this.handleDurdur();

        // Geçici çizgiyi hemen sil (ngOnDestroy'u beklemeden)
        // Bu çok önemli çünkü pencere.kapa() ngOnDestroy'u hemen tetiklemeyebilir
        this.geciciCizgiSil();

        if (this.pencere && typeof this.pencere.kapa === 'function') {
            try {
                this.pencere.kapa();
            } catch (e) {
                console.warn('Dialog kapatılamadı:', e);
            }
        } else {
            this.kapat();
        }
    }

    /**
     * Formu sıfırla
     */
    sifirla(): void {
        this.seciliYontem = 'noktadanNoktaya';
        this.isim = '';  // Boş bırakılırsa otomatik numaralandırılır
        this.baslangicX = 0;
        this.baslangicY = 0;
        this.baslangicZ = 0;
        this.bitisX = 100;
        this.bitisY = 0;
        this.bitisZ = 0;
        this.yonX = 1;
        this.yonY = 0;
        this.yonZ = 0;
        this.uzunluk = 100;
        this.aciDerece = 0;
        this.thetaDerece = 0;
        this.phiDerece = 90;
        this.cizgiTipi = 'Line';
        this.renk = '#00ff00';
        this.kalinlik = 1;
        this.stil = 'duz';
        this.kesikliAralik = 5;
        this.kesikliBoyut = 10;
        this.opaklik = 1;
        this.okBaslangic = 'yok';
        this.okBitis = 'yok';
        this.okBoyut = 0.1;
        this.secilebilir = true;
        this.katman = 0;
        this.enUstte = false;
        this.renderEt = true;
    }

    /**
     * Yöntem değiştiğinde mevcut çizgi durumundan diğer yöntemlerin değerlerini hesapla
     * Bu sayede kullanıcı yöntemler arası geçiş yaptığında değerler senkronize olur
     */
    yontemDegisti(yeniYontem: CizgiOlusturmaYontemi): void {
        // Mevcut başlangıç ve bitiş noktalarını al
        // (geçici çizgi, mevcut çizgi veya form değerlerinden)
        let baslangic: THREE.Vector3;
        let bitis: THREE.Vector3;

        if (this.geciciCizgiAktif && this.geciciCizgi) {
            baslangic = this.geciciCizgi.baslangic.clone();
            bitis = this.geciciCizgi.bitis.clone();
        } else if (this.duzenleModu && this.mevcutCizgi) {
            baslangic = this.mevcutCizgi.baslangic.clone();
            bitis = this.mevcutCizgi.bitis.clone();
        } else {
            baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
            bitis = new THREE.Vector3(this.bitisX, this.bitisY, this.bitisZ);
        }

        // Yön vektörü ve uzunluk hesapla
        const yon = bitis.clone().sub(baslangic);
        const uzunluk = yon.length();
        if (uzunluk > 0.001) {
            yon.normalize();
        } else {
            yon.set(1, 0, 0); // Varsayılan yön
        }

        // Tüm değerleri mevcut çizgiden senkronize et
        // Başlangıç noktası
        this.baslangicX = Math.round(baslangic.x * 100) / 100;
        this.baslangicY = Math.round(baslangic.y * 100) / 100;
        this.baslangicZ = Math.round(baslangic.z * 100) / 100;

        // Bitiş noktası
        this.bitisX = Math.round(bitis.x * 100) / 100;
        this.bitisY = Math.round(bitis.y * 100) / 100;
        this.bitisZ = Math.round(bitis.z * 100) / 100;

        // Uzunluk
        this.uzunluk = Math.round(uzunluk * 100) / 100;

        // Yön vektörü (noktadanYonUzunluk ve merkezdenCift için)
        this.yonX = Math.round(yon.x * 10000) / 10000;
        this.yonY = Math.round(yon.y * 10000) / 10000;
        this.yonZ = Math.round(yon.z * 10000) / 10000;

        // 2D Açı hesapla (noktadanAciUzunluk için - XY düzleminde)
        // atan2(y, x) açıyı radyan cinsinden verir
        this.aciDerece = Math.round(Math.atan2(yon.y, yon.x) * 180 / Math.PI * 100) / 100;

        // 3D Küresel açılar hesapla (noktadan3DAci için)
        // theta: XZ düzleminde X ekseninden saat yönünde açı
        // phi: Y ekseninden aşağı doğru açı (0 = yukarı, 90 = yatay, 180 = aşağı)
        this.thetaDerece = Math.round(Math.atan2(yon.z, yon.x) * 180 / Math.PI * 100) / 100;
        // phi = acos(y / length) - y bileşeni cos(phi)'ye karşılık gelir
        const phiRad = Math.acos(Math.max(-1, Math.min(1, yon.y)));
        this.phiDerece = Math.round(phiRad * 180 / Math.PI * 100) / 100;

        // Yönteme özel ayarlamalar
        switch (yeniYontem) {
            case 'yatay':
                // Y ve Z sabit, sadece X değişir
                this.bitisY = this.baslangicY;
                this.bitisZ = this.baslangicZ;
                break;

            case 'dikey':
                // X ve Z sabit, sadece Y değişir
                this.bitisX = this.baslangicX;
                this.bitisZ = this.baslangicZ;
                break;

            case 'derinlik':
                // X ve Y sabit, sadece Z değişir
                this.bitisX = this.baslangicX;
                this.bitisY = this.baslangicY;
                break;

            case 'merkezdenCift':
                // Merkez noktayı hesapla (başlangıç ve bitiş ortası)
                const merkez = baslangic.clone().add(bitis).multiplyScalar(0.5);
                this.baslangicX = Math.round(merkez.x * 100) / 100;
                this.baslangicY = Math.round(merkez.y * 100) / 100;
                this.baslangicZ = Math.round(merkez.z * 100) / 100;
                break;
        }

        // Çizgiyi anlık güncelle
        this.cizgiyiAnlikGuncelle();

        // UI'ı güncelle
        this.cd.detectChanges();
    }

    /**
     * Çizgi ayarlarını oluştur
     */
    private ayarlariOlustur(): Partial<ICizgiAyarlari> {
        return {
            renk: this.renk,
            kalinlik: this.kalinlik,
            stil: this.stil,
            kesikliAralik: this.kesikliAralik,
            kesikliBoyut: this.kesikliBoyut,
            opaklık: this.opaklik,
            okBaslangic: this.okBaslangic,
            okBitis: this.okBitis,
            okBoyut: this.okBoyut,
            secilebilir: this.secilebilir,
            katman: this.katman,
            cizgiTipi: this.cizgiTipi,
            enUstte: this.enUstte,
            renderEt: this.renderEt
        };
    }

    /**
     * Çizgiyi oluştur veya güncelle
     */
    olusturVeyaGuncelle(): void {
        if (this.duzenleModu && this.mevcutCizgi) {
            this.guncelle();
        } else {
            this.olustur();
        }
    }

    /**
     * Yeni çizgi oluştur
     */
    private olustur(): void {
        let cizgi: FikoYT3dLine;

        // Geçici çizgi varsa onu kalıcı yap (yeni yöntem - daha verimli)
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            const kaliciCizgi = this.geciciCizgiyiKaliciYap();
            if (kaliciCizgi) {
                cizgi = kaliciCizgi;
                // Yöntemi kaydet
                cizgi.yontem = this.seciliYontem;

                // Aktif body'ye ekle (ürün ağacında görünmesi için)
                if (this.aktifBodyeEkle && this.seciliAktifBody) {
                    this.cizgiyiBodyeEkle(cizgi, this.seciliAktifBody);
                }

                // Event emit et
                this.cizgiOlusturuldu.emit(cizgi);

                // Callback varsa çağır (FikoDialogService kullanımı)
                if (this.cizgiOlusturulduCallback) {
                    this.cizgiOlusturulduCallback(cizgi);
                }

                // Dialog'u kapat
                this.dialogKapat();
                return;
            }
        }

        // Geçici çizgi yoksa (fallback) yeni çizgi oluştur
        const ayarlar = this.ayarlariOlustur();

        switch (this.seciliYontem) {
            case 'noktadanNoktaya':
                cizgi = FikoYT3dLine.noktadanNoktaya(
                    new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ),
                    new THREE.Vector3(this.bitisX, this.bitisY, this.bitisZ),
                    ayarlar,
                    this.sahne,
                    this.isim
                );
                break;

            case 'noktadanYonUzunluk':
                cizgi = FikoYT3dLine.noktadanYonUzunluk(
                    new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ),
                    new THREE.Vector3(this.yonX, this.yonY, this.yonZ),
                    this.uzunluk,
                    ayarlar,
                    this.sahne,
                    this.isim
                );
                break;

            case 'noktadanAciUzunluk':
                cizgi = FikoYT3dLine.noktadanAciUzunluk(
                    new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ),
                    this.aciDerece,
                    this.uzunluk,
                    ayarlar,
                    this.sahne,
                    this.isim
                );
                break;

            case 'noktadan3DAci':
                cizgi = FikoYT3dLine.noktadan3DAci(
                    new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ),
                    this.thetaDerece,
                    this.phiDerece,
                    this.uzunluk,
                    ayarlar,
                    this.sahne,
                    this.isim
                );
                break;

            case 'yatay':
                cizgi = FikoYT3dLine.yatay(
                    this.baslangicX,
                    this.bitisX,
                    this.baslangicY,
                    this.baslangicZ,
                    ayarlar,
                    this.sahne,
                    this.isim
                );
                break;

            case 'dikey':
                cizgi = FikoYT3dLine.dikey(
                    this.baslangicY,
                    this.bitisY,
                    this.baslangicX,
                    this.baslangicZ,
                    ayarlar,
                    this.sahne,
                    this.isim
                );
                break;

            case 'derinlik':
                cizgi = FikoYT3dLine.derinlik(
                    this.baslangicZ,
                    this.bitisZ,
                    this.baslangicX,
                    this.baslangicY,
                    ayarlar,
                    this.sahne,
                    this.isim
                );
                break;

            case 'merkezdenCift':
                cizgi = FikoYT3dLine.merkezdenCift(
                    new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ),
                    new THREE.Vector3(this.yonX, this.yonY, this.yonZ),
                    this.uzunluk,
                    ayarlar,
                    this.sahne,
                    this.isim
                );
                break;

            default:
                return;
        }

        // Yöntemi kaydet
        cizgi.yontem = this.seciliYontem;

        // Aktif body'ye ekle (ürün ağacında görünmesi için)
        if (this.aktifBodyeEkle && this.seciliAktifBody) {
            this.cizgiyiBodyeEkle(cizgi, this.seciliAktifBody);
        }

        // Event emit et
        this.cizgiOlusturuldu.emit(cizgi);

        // Callback varsa çağır (FikoDialogService kullanımı)
        if (this.cizgiOlusturulduCallback) {
            this.cizgiOlusturulduCallback(cizgi);
        }

        // Dialog'u kapat
        this.dialogKapat();
    }

    /**
     * Çizgiyi aktif body'ye ekle ve ürün ağacını güncelle
     */
    private cizgiyiBodyeEkle(cizgi: FikoYT3dLine, body: any): void {
        if (!cizgi || !body) return;

        try {
            // Sahne referansını ayarla - EN ÖNCE yapılmalı (agac-item tıklama için gerekli)
            const sahneRef = body.sahne || this.sahne || this.anaGosterim?.sahneler?.[0];
            if (sahneRef) {
                (cizgi as any).sahne = sahneRef;
            }

            // Çizgi'nin userData.FikoArayuz'unu ayarla (ürün ağacında görünmesi için)
            if (!cizgi.userData) cizgi.userData = {};
            cizgi.userData.FikoArayuz = {
                agactaGorunur: true,
                listeGoster: false,
                simge: '/simgeler/line.png'
            };

            // Body bir THREE.Object3D ise doğrudan ekle (3D sahneye ekleme)
            if (body.add && typeof body.add === 'function') {
                body.add(cizgi);
            }

            // Body'nin liste array'ine de ekle (ürün ağacı navigasyonu için)
            // NOT: body.liste bir getter olabilir, bu yüzden dikkatli olmalıyız
            try {
                if (body._liste && Array.isArray(body._liste)) {
                    // Private backing field varsa ona ekle
                    if (!body._liste.includes(cizgi)) {
                        body._liste.push(cizgi);
                    }
                } else if (body.liste && Array.isArray(body.liste)) {
                    // Doğrudan liste array'ine ekle
                    if (!body.liste.includes(cizgi)) {
                        body.liste.push(cizgi);
                    }
                } else if (body.children && Array.isArray(body.children)) {
                    // THREE.Object3D children kullan (body.add zaten bunu yapar ama emin ol)
                    // Liste olmasa bile children üzerinden görünür
                }
            } catch (listeErr) {
                // Liste ekleme hatası (getter olabilir) - görmezden gel
            }

            // NOT: ciftTiklandi metodu FikoYT3dLine sınıfında zaten tanımlı
            // Burada override ETMİYORUZ - duplicate dialog sorununa yol açıyor
            // Çizgi kendi ciftTiklandi metodunu kullanacak

            // FikoYT.degisti emit et - ürün ağacının güncellenmesi için KRİTİK
            this.fikoYT.degisti.emit();

            // Change detection tetikle
            this.cd.detectChanges();

        } catch (error) {
            // Body'ye ekleme hatası - görmezden gel
        }
    }

    /**
     * Çizgi düzenleme dialogunu aç
     * NOT: Bu metod artık kullanılmamalı - FikoYT3dLine.ciftTiklandi() tercih edilmeli
     * Geriye dönük uyumluluk için bırakılıyor ama duplicate kontrolü eklendi
     */
    private cizgiDuzenleDialoguAc(cizgi: FikoYT3dLine, anaGosterim: any): void {
        // DialogFiko servisini kullanarak düzenleme dialogu aç
        const dialogService = anaGosterim?.DialogFiko;
        if (!dialogService) return;

        // SABİT ID kullan - Date.now() duplicate dialoga yol açıyordu!
        const dialogId = 'fiko-3d-line-edit-' + cizgi.uuid;

        // Mevcut dialog var mı kontrol et
        const mevcutDialog = dialogService.idIleAl?.(dialogId);
        if (mevcutDialog && mevcutDialog.miAcik) {
            // Dialog zaten açık, sayfadaki en yüksek z-index'i bulup öne getir
            const dialogEl = document.getElementById(mevcutDialog.id_dialog);
            if (dialogEl) {
                // overlayZIndexAl: Sayfadaki TÜM elementleri tarar
                const yeniZIndex = dialogService.overlayZIndexAl?.() || 99999;
                dialogEl.style.setProperty('z-index', yeniZIndex.toString(), 'important');
                const parent = dialogEl.parentElement;
                if (parent) {
                    parent.style.setProperty('z-index', yeniZIndex.toString(), 'important');
                }
                dialogService.dialogTiklandi?.(mevcutDialog);
                dialogEl.focus();
            }
            return;
        }

        dialogService.ac({
            bilesen: FikoYT3dLineDialogComponent,
            id: dialogId,
            ayarlar: {
                baslik: 'Çizgi Düzenle: ' + cizgi.name,
                simge: 'pencil',
                modalMi: false,
                sinirlandi: false,
                boyut: { w: 220, h: 'auto' }
            },
            parametreler: {
                anaGosterim: anaGosterim,
                duzenleModu: true,
                mevcutCizgi: cizgi
            }
        });
    }

    /**
     * Mevcut çizgiyi güncelle
     */
    private guncelle(): void {
        if (!this.mevcutCizgi) return;

        // Seçilen yönteme göre başlangıç ve bitiş noktalarını hesapla
        let baslangic: THREE.Vector3;
        let bitis: THREE.Vector3;

        switch (this.seciliYontem) {
            case 'noktadanNoktaya':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.bitisY, this.bitisZ);
                break;

            case 'noktadanYonUzunluk':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const yon = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                bitis = baslangic.clone().add(yon.multiplyScalar(this.uzunluk));
                break;

            case 'noktadanAciUzunluk':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const aciRad = this.aciDerece * Math.PI / 180;
                bitis = new THREE.Vector3(
                    baslangic.x + Math.cos(aciRad) * this.uzunluk,
                    baslangic.y + Math.sin(aciRad) * this.uzunluk,
                    baslangic.z
                );
                break;

            case 'noktadan3DAci':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const theta = this.thetaDerece * Math.PI / 180;
                const phi = this.phiDerece * Math.PI / 180;
                bitis = new THREE.Vector3(
                    baslangic.x + this.uzunluk * Math.sin(phi) * Math.cos(theta),
                    baslangic.y + this.uzunluk * Math.cos(phi),
                    baslangic.z + this.uzunluk * Math.sin(phi) * Math.sin(theta)
                );
                break;

            case 'yatay':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.baslangicY, this.baslangicZ);
                break;

            case 'dikey':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.baslangicX, this.bitisY, this.baslangicZ);
                break;

            case 'derinlik':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.baslangicX, this.baslangicY, this.bitisZ);
                break;

            case 'merkezdenCift':
                const merkez = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const yonCift = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                const yarimUzunluk = this.uzunluk / 2;
                baslangic = merkez.clone().sub(yonCift.clone().multiplyScalar(yarimUzunluk));
                bitis = merkez.clone().add(yonCift.clone().multiplyScalar(yarimUzunluk));
                break;

            default:
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.bitisY, this.bitisZ);
        }

        // Pozisyonları güncelle
        this.mevcutCizgi.baslangic = baslangic;
        this.mevcutCizgi.bitis = bitis;

        // Ayarları güncelle
        this.mevcutCizgi.renk = this.renk;
        this.mevcutCizgi.kalinlik = this.kalinlik;
        this.mevcutCizgi.stil = this.stil;
        this.mevcutCizgi.opaklık = this.opaklik;
        this.mevcutCizgi.name = this.isim;
        this.mevcutCizgi.cizgiTipi = this.cizgiTipi;
        this.mevcutCizgi.yontem = this.seciliYontem;

        // Diğer ayarları güncelle
        this.mevcutCizgi.secilebilir = this.secilebilir;
        this.mevcutCizgi.katman = this.katman;
        this.mevcutCizgi.enUstte = this.enUstte;
        this.mevcutCizgi.renderEt = this.renderEt;

        this.cizgiGuncellendi.emit(this.mevcutCizgi);
        this.dialogKapat();
    }

    /**
     * Renk hex değerini sayıya çevir
     */
    private hexToNumber(hex: string): number {
        return parseInt(hex.replace('#', ''), 16);
    }

    /**
     * Çizgiyi sil
     */
    cizgiSil(): void {
        if (!this.mevcutCizgi) return;

        // Çizgiyi sil
        this.mevcutCizgi.sil();

        // Event emit et
        this.cizgiSilindi.emit(this.mevcutCizgi);

        // Dialog'u kapat
        this.dialogKapat();
    }

    // ========================================================================
    // HANDLE YÖNETİMİ (CATIA-Style Draggable Handles)
    // ========================================================================

    /** Handle başlatma retry sayacı */
    private handleBaslatRetryCount: number = 0;
    private readonly HANDLE_MAX_RETRY = 5;
    private readonly HANDLE_RETRY_DELAY = 100; // ms

    /** Pending timeout ID'leri - iptal edilebilmesi için */
    private handleBaslatTimeoutId?: ReturnType<typeof setTimeout>;
    private handleGeciciTimeoutId?: ReturnType<typeof setTimeout>;

    /**
     * Handle sistemini başlat
     * Düzenleme modunda çizgi uç noktalarında sürüklenebilir handle'lar gösterir
     */
    handleBaslat(): void {
        console.log('[Handle Debug] handleBaslat() çağrıldı', {
            mevcutCizgi: !!this.mevcutCizgi,
            sahne: !!this.sahne,
            anaGosterim: !!this.anaGosterim
        });

        if (!this.mevcutCizgi) {
            console.warn('[Handle] mevcutCizgi yok, handle başlatılamıyor');
            return;
        }

        // Önce mevcut handle'ları temizle (orphan'lar dahil)
        this.handleDurdur();

        // Sahneyi al - öncelik sırası:
        // 1. Çizginin kendi sahne referansı (en güvenilir)
        // 2. Dialog parametresindeki sahne
        // 3. anaGosterim.sahneler[0] (fallback)
        let sahneObj = (this.mevcutCizgi as any)?.sahne || this.sahne || this.anaGosterim?.sahneler?.[0];

        // Eğer sahne hala bulunamadıysa, çizginin parent'ını traverse ederek bul
        if (!sahneObj && this.mevcutCizgi?.parent) {
            let parent = this.mevcutCizgi.parent;
            while (parent && parent.type !== 'Scene') {
                parent = parent.parent as any;
            }
            if (parent) sahneObj = parent;
        }

        if (!sahneObj) {
            // Sahne henüz hazır değil, retry yap
            if (this.handleBaslatRetryCount < this.HANDLE_MAX_RETRY) {
                this.handleBaslatRetryCount++;
                console.warn(`[Handle] Sahne hazır değil, retry ${this.handleBaslatRetryCount}/${this.HANDLE_MAX_RETRY}`);
                this.handleBaslatTimeoutId = setTimeout(() => this.handleBaslat(), this.HANDLE_RETRY_DELAY);
            } else {
                console.error('[Handle] Sahne bulunamadı, maksimum retry aşıldı');
                this.handleBaslatRetryCount = 0; // Reset for next attempt
            }
            return;
        }

        // Canvas'ı kontrol et
        const canvas = this.canvasBul();
        if (!canvas) {
            // Canvas henüz hazır değil, retry yap
            if (this.handleBaslatRetryCount < this.HANDLE_MAX_RETRY) {
                this.handleBaslatRetryCount++;
                console.warn(`[Handle] Canvas hazır değil, retry ${this.handleBaslatRetryCount}/${this.HANDLE_MAX_RETRY}`);
                this.handleBaslatTimeoutId = setTimeout(() => this.handleBaslat(), this.HANDLE_RETRY_DELAY);
            } else {
                console.error('[Handle] Canvas bulunamadı, maksimum retry aşıldı');
                this.handleBaslatRetryCount = 0; // Reset for next attempt
            }
            return;
        }

        // Retry başarılı, sayacı sıfırla
        this.handleBaslatRetryCount = 0;

        // Handle'ları oluştur
        this.handles = new FikoYT3dLineHandles(this.mevcutCizgi);

        console.log('[Handle Debug] Yeni handle oluşturuldu', {
            handleIsmi: this.handles.name,
            cizgiUuid: this.mevcutCizgi.uuid,
            cizgiIsmi: this.mevcutCizgi.name
        });

        // Sahneye ekle
        sahneObj.add(this.handles);

        // Handle'ın görünür olduğundan emin ol
        this.handles.visible = true;
        this.handles.traverse((child: THREE.Object3D) => {
            child.visible = true;
            child.frustumCulled = false;
        });
        this.handles.guncelle();

        this.handleAktif = true;

        // Event handler referanslarını oluştur
        this.handleMouseDownRef = (e: MouseEvent) => this.handleMouseDown(e);
        this.handleMouseMoveRef = (e: MouseEvent) => this.handleMouseMove(e);
        this.handleMouseUpRef = (e: MouseEvent) => this.handleMouseUp(e);

        // Event listener'ları ekle
        canvas.addEventListener('mousedown', this.handleMouseDownRef);
        canvas.addEventListener('mousemove', this.handleMouseMoveRef);
        canvas.addEventListener('mouseup', this.handleMouseUpRef);

        // Render'ı tetikle (handle'ların görünmesi için)
        if (this.anaGosterim?.sunucu && this.anaGosterim?.aktifKamera) {
            this.anaGosterim.sunucu.render(sahneObj, this.anaGosterim.aktifKamera);
        }

        console.log('[Handle] Handle\'lar başarıyla başlatıldı');
    }

    /**
     * Handle sistemini durdur
     */
    handleDurdur(): void {
        // Pending timeout'ları iptal et - race condition önleme
        if (this.handleBaslatTimeoutId) {
            clearTimeout(this.handleBaslatTimeoutId);
            this.handleBaslatTimeoutId = undefined;
        }
        if (this.handleGeciciTimeoutId) {
            clearTimeout(this.handleGeciciTimeoutId);
            this.handleGeciciTimeoutId = undefined;
        }

        // Retry sayaçlarını sıfırla - eski retry'lar yeni handle'ları etkilemesin
        this.handleBaslatRetryCount = 0;
        this.handleGeciciRetryCount = 0;

        // Referansları önce al, sonra temizle
        const mouseDownRef = this.handleMouseDownRef;
        const mouseMoveRef = this.handleMouseMoveRef;
        const mouseUpRef = this.handleMouseUpRef;

        // Referansları hemen null yap (tekrar çağrılma durumunda)
        this.handleMouseDownRef = undefined;
        this.handleMouseMoveRef = undefined;
        this.handleMouseUpRef = undefined;
        this.handleAktif = false;

        // Event listener'ları kaldır - tüm olası canvas'lardan
        const canvas = this.canvasBul();
        if (canvas) {
            if (mouseDownRef) canvas.removeEventListener('mousedown', mouseDownRef);
            if (mouseMoveRef) canvas.removeEventListener('mousemove', mouseMoveRef);
            if (mouseUpRef) canvas.removeEventListener('mouseup', mouseUpRef);
        }

        // Document'tan da kaldır (bazen document'a da ekleniyor olabilir)
        if (mouseDownRef) document.removeEventListener('mousedown', mouseDownRef);
        if (mouseMoveRef) document.removeEventListener('mousemove', mouseMoveRef);
        if (mouseUpRef) document.removeEventListener('mouseup', mouseUpRef);

        // Handle'ları temizle
        if (this.handles) {
            const handlesToClean = this.handles;
            this.handles = undefined;

            // Handle'ın parent'ından kaldır (en güvenilir yol)
            if (handlesToClean.parent) {
                handlesToClean.parent.remove(handlesToClean);
            }

            // Ek olarak bilinen tüm sahnelerden kaldırmayı dene
            // Geçici çizgi için de kontrol et
            const hedefCizgi = this.geciciCizgiAktif ? this.geciciCizgi : this.mevcutCizgi;
            const sahneObj = (hedefCizgi as any)?.sahne ||
                            (this.geciciCizgi as any)?.parent ||
                            (this.mevcutCizgi as any)?.sahne ||
                            this.sahne ||
                            this.anaGosterim?.sahneler?.[0];
            if (sahneObj) {
                sahneObj.remove(handlesToClean);
            }

            // Kaynakları temizle
            try {
                handlesToClean.temizle();
            } catch (e) {
                console.warn('Handle temizleme hatası:', e);
            }
        }

        // Sahnedeki orphan handle'ları da temizle
        this.orphanHandleTemizle();
    }

    /**
     * Sahnede kalan orphan handle'ları temizle
     * NOT: Sadece BU çizginin handle'larını temizler, diğer çizgilerin handle'larına dokunmaz!
     */
    private orphanHandleTemizle(): void {
        // Hedef çizgiyi belirle (geçici veya mevcut)
        const hedefCizgi = this.geciciCizgiAktif ? this.geciciCizgi : this.mevcutCizgi;

        // Eğer hedef çizgi yoksa temizleyecek bir şey yok
        if (!hedefCizgi) return;

        // Bu çizgiye ait handle ismi
        const buCizgiHandleIsmi = 'LineHandles_' + hedefCizgi.uuid;

        // Tüm olası sahnelerde BU ÇİZGİNİN handle'larını temizle
        const sahneler = [
            (hedefCizgi as any)?.sahne,
            (this.mevcutCizgi as any)?.sahne,
            (this.geciciCizgi as any)?.parent, // Geçici çizginin parent'ı sahne
            this.sahne,
            this.anaGosterim?.sahneler?.[0]
        ].filter(Boolean);

        if (sahneler.length === 0) return;

        // Benzersiz sahneleri al
        const benzersizSahneler = [...new Set(sahneler)];

        for (const sahne of benzersizSahneler) {
            const eskiHandles: THREE.Object3D[] = [];
            sahne.traverse((child: THREE.Object3D) => {
                // SADECE bu çizginin handle'larını temizle!
                // Diğer çizgilerin handle'larına dokunma
                if (child.name === buCizgiHandleIsmi) {
                    eskiHandles.push(child);
                }
            });

            for (const eski of eskiHandles) {
                console.log('[Handle Debug] orphan temizleniyor:', eski.name);
                sahne.remove(eski);
                if ((eski as any).temizle) {
                    try {
                        (eski as any).temizle();
                    } catch (e) {
                        // ignore
                    }
                }
            }
        }
    }

    /**
     * Handle üzerinde mouse down
     * Ekran koordinatı tabanlı hit detection (Points için en güvenilir yöntem)
     */
    private handleMouseDown(event: MouseEvent): void {
        if (!this.handles || !this.handleAktif) return;

        // Hedef çizgiyi belirle (geçici veya mevcut)
        const hedefCizgi = this.geciciCizgiAktif ? this.geciciCizgi : this.mevcutCizgi;
        if (!hedefCizgi) return;

        const canvas = this.canvasBul();
        if (!canvas) return;

        const camera = this.anaGosterim?.aktifKamera;
        if (!camera) return;

        // Handle'ların world matrix'lerini güncelle
        this.handles.updateMatrixWorld(true);

        // Ekran koordinatlarını al
        const rect = canvas.getBoundingClientRect();
        const screenX = event.clientX - rect.left;
        const screenY = event.clientY - rect.top;

        // Raycaster ayarla (hem hit detection hem de drag plane için gerekli)
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        this.handleRaycaster.setFromCamera(new THREE.Vector2(x, y), camera);
        this.handleRaycaster.near = 0.001;
        this.handleRaycaster.far = 1000000;

        // Bulunan handle tip'ini belirle - önce ekran koordinatı tabanlı hit test (en güvenilir)
        let handleTip: 'baslangic' | 'bitis' | 'orta' | null = null;

        // Ekran koordinatı tabanlı hit detection (sizeAttenuation: false için ideal)
        const screenHit = this.handles.getHandleAtScreenPosition(screenX, screenY, camera, rect.width, rect.height);
        if (screenHit.tip) {
            handleTip = screenHit.tip as 'baslangic' | 'bitis' | 'orta';
        } else {
            // Fallback: Manuel küre kesişim testi (3D ray-sphere intersection)
            const manuelSonuc = this.handles.manuelKureKesisim(this.handleRaycaster.ray);
            if (manuelSonuc.hit && manuelSonuc.handleTip) {
                handleTip = manuelSonuc.handleTip as 'baslangic' | 'bitis' | 'orta';
            }
        }

        // Handle bulunduysa sürüklemeyi başlat
        if (handleTip) {

            // Sürükleme başlat
            this.handles.isDragging = true;
            this.handles.dragTarget = handleTip;

            // Sürüklenen uç noktanın pozisyonunu al (çizginin baslangic/bitis zaten world koordinatlarında)
            let handleWorldPos: THREE.Vector3;
            if (handleTip === 'baslangic') {
                handleWorldPos = hedefCizgi.baslangic.clone();
            } else if (handleTip === 'bitis') {
                handleWorldPos = hedefCizgi.bitis.clone();
            } else {
                handleWorldPos = hedefCizgi.ortaNokta.clone();
            }

            // Kamera-paralel sürükleme düzlemi oluştur (çizgi ucunun world pozisyonunda)
            const cameraDir = new THREE.Vector3();
            camera.getWorldDirection(cameraDir);
            this.dragPlane.setFromNormalAndCoplanarPoint(cameraDir.clone().negate(), handleWorldPos);

            // Başlangıç pozisyonunu kaydet - sürükleme delta hesabı için
            // Plane ile kesişim noktasını bul (tıklama anındaki)
            const startIntersect = new THREE.Vector3();
            let kesisimBulundu = this.handleRaycaster.ray.intersectPlane(this.dragPlane, startIntersect);

            // Eğer kamera-paralel plane ile kesişim bulunamazsa, alternatif düzlem kullan
            if (!kesisimBulundu) {
                const absX = Math.abs(cameraDir.x);
                const absY = Math.abs(cameraDir.y);
                const absZ = Math.abs(cameraDir.z);

                let planeNormal: THREE.Vector3;
                if (absZ >= absX && absZ >= absY) {
                    planeNormal = new THREE.Vector3(0, 0, 1);
                } else if (absY >= absX && absY >= absZ) {
                    planeNormal = new THREE.Vector3(0, 1, 0);
                } else {
                    planeNormal = new THREE.Vector3(1, 0, 0);
                }

                // Ana drag plane'i de güncelle (move sırasında kullanılacak)
                this.dragPlane.setFromNormalAndCoplanarPoint(planeNormal, handleWorldPos);
                kesisimBulundu = this.handleRaycaster.ray.intersectPlane(this.dragPlane, startIntersect);
            }

            if (kesisimBulundu) {
                // Offset: tıklama noktası ile çizgi ucu arasındaki fark
                this.dragOffset.copy(startIntersect).sub(handleWorldPos);
            } else {
                // Kesişim bulunamazsa offset sıfırla
                this.dragOffset.set(0, 0, 0);
            }

            // Orbit kontrollerini devre dışı bırak
            const kontrol = this.anaGosterim?.aktifKontrol;
            if (kontrol) {
                kontrol.enabled = false;
            }

            // Cursor değiştir
            canvas.style.cursor = 'grabbing';

            // Event'i durdur (diğer tıklama işlemlerini engelle)
            event.preventDefault();
            event.stopPropagation();
        }
    }

    /**
     * Handle sürüklerken mouse move
     */
    private handleMouseMove(event: MouseEvent): void {
        if (!this.handles || !this.handleAktif) return;

        const canvas = this.canvasBul();
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const camera = this.anaGosterim?.aktifKamera;
        if (!camera) return;

        // Sürükleme modunda değilse sadece hover kontrolü yap
        if (!this.handles.isDragging) {
            // Handle'ların world matrix'lerini güncelle (kamera döndüğünde gerekli)
            this.handles.updateMatrixWorld(true);

            // Ekran koordinatları
            const screenX = event.clientX - rect.left;
            const screenY = event.clientY - rect.top;

            // Ekran koordinatı tabanlı hit detection (en güvenilir)
            let hover = false;
            const screenHit = this.handles.getHandleAtScreenPosition(screenX, screenY, camera, rect.width, rect.height);
            if (screenHit.tip) {
                this.handles.setHoverByTip(screenHit.tip as 'baslangic' | 'bitis' | 'orta');
                hover = true;
            } else {
                // Fallback: Manuel küre kesişim testi
                const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
                const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
                this.handleRaycaster.setFromCamera(new THREE.Vector2(x, y), camera);
                this.handleRaycaster.near = 0.001;
                this.handleRaycaster.far = 1000000;

                const manuelSonuc = this.handles.manuelKureKesisim(this.handleRaycaster.ray);
                if (manuelSonuc.hit) {
                    this.handles.setHoverByTip(manuelSonuc.handleTip as 'baslangic' | 'bitis' | 'orta' | null);
                    hover = true;
                } else {
                    this.handles.setHoverByTip(null);
                }
            }

            canvas.style.cursor = hover ? 'grab' : 'default';
            return;
        }

        // Sürükleme modunda - pozisyonu güncelle
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        this.handleRaycaster.setFromCamera(new THREE.Vector2(x, y), camera);
        this.handleRaycaster.near = 0.001;
        this.handleRaycaster.far = 1000000;

        const intersectPoint = new THREE.Vector3();
        let kesisimBulundu = this.handleRaycaster.ray.intersectPlane(this.dragPlane, intersectPoint);

        // Eğer kamera-paralel plane ile kesişim bulunamazsa, alternatif düzlemler dene
        if (!kesisimBulundu) {
            // Kamera açısına göre en uygun düzlemi seç
            const cameraDir = new THREE.Vector3();
            camera.getWorldDirection(cameraDir);

            // Hedef çizgiyi al
            const hedefCizgi = this.geciciCizgiAktif ? this.geciciCizgi : this.mevcutCizgi;
            if (!hedefCizgi) return;

            // Sürüklenen noktanın mevcut pozisyonunu al
            let handlePos: THREE.Vector3;
            if (this.handles.dragTarget === 'baslangic') {
                handlePos = hedefCizgi.baslangic.clone();
            } else if (this.handles.dragTarget === 'bitis') {
                handlePos = hedefCizgi.bitis.clone();
            } else {
                handlePos = hedefCizgi.ortaNokta.clone();
            }

            // Kamera yönüne göre en dik düzlemi seç (XY, XZ veya YZ)
            const absX = Math.abs(cameraDir.x);
            const absY = Math.abs(cameraDir.y);
            const absZ = Math.abs(cameraDir.z);

            let planeNormal: THREE.Vector3;
            if (absZ >= absX && absZ >= absY) {
                // Kamera Z eksenine bakıyor -> XY düzlemi kullan
                planeNormal = new THREE.Vector3(0, 0, 1);
            } else if (absY >= absX && absY >= absZ) {
                // Kamera Y eksenine bakıyor -> XZ düzlemi kullan
                planeNormal = new THREE.Vector3(0, 1, 0);
            } else {
                // Kamera X eksenine bakıyor -> YZ düzlemi kullan
                planeNormal = new THREE.Vector3(1, 0, 0);
            }

            const alternatifPlane = new THREE.Plane();
            alternatifPlane.setFromNormalAndCoplanarPoint(planeNormal, handlePos);
            kesisimBulundu = this.handleRaycaster.ray.intersectPlane(alternatifPlane, intersectPoint);
        }

        if (!kesisimBulundu) {
            return;
        }

        // Yeni pozisyonu hesapla: plane intersection noktasından offset'i çıkar
        const yeniPozisyon = new THREE.Vector3().copy(intersectPoint).sub(this.dragOffset);

        // Yönteme göre kısıtlamalar uygula ve çizgiyi güncelle
        this.handlePozisyonGuncelle(this.handles.dragTarget!, yeniPozisyon);

        // Event'i durdur
        event.preventDefault();
    }

    /**
     * Handle bırakıldığında mouse up
     */
    private handleMouseUp(event: MouseEvent): void {
        if (!this.handles) return;

        if (this.handles.isDragging) {
            this.handles.isDragging = false;
            this.handles.dragTarget = null;

            // Orbit kontrollerini yeniden etkinleştir
            const kontrol = this.anaGosterim?.aktifKontrol;
            if (kontrol) {
                kontrol.enabled = true;
            }

            // Cursor sıfırla
            const canvas = this.canvasBul();
            if (canvas) {
                canvas.style.cursor = 'default';
            }
        }
    }

    /**
     * Handle pozisyonunu güncelle ve form değerlerini senkronize et
     */
    private handlePozisyonGuncelle(hedef: 'baslangic' | 'bitis' | 'orta', yeniPozisyon: THREE.Vector3): void {
        // Geçici çizgi veya mevcut çizgi olmalı
        const hedefCizgi = this.geciciCizgiAktif ? this.geciciCizgi : this.mevcutCizgi;
        if (!hedefCizgi || !this.handles) return;

        // Koordinatları yuvarla
        const roundedX = Math.round(yeniPozisyon.x * 100) / 100;
        const roundedY = Math.round(yeniPozisyon.y * 100) / 100;
        const roundedZ = Math.round(yeniPozisyon.z * 100) / 100;

        // Yönteme göre güncelleme yap
        switch (this.seciliYontem) {
            case 'noktadanNoktaya':
                if (hedef === 'baslangic') {
                    this.baslangicX = roundedX;
                    this.baslangicY = roundedY;
                    this.baslangicZ = roundedZ;
                    hedefCizgi.baslangic = new THREE.Vector3(roundedX, roundedY, roundedZ);
                } else if (hedef === 'bitis') {
                    this.bitisX = roundedX;
                    this.bitisY = roundedY;
                    this.bitisZ = roundedZ;
                    hedefCizgi.bitis = new THREE.Vector3(roundedX, roundedY, roundedZ);
                }
                break;

            case 'noktadanYonUzunluk':
                if (hedef === 'baslangic') {
                    // Başlangıç noktasını taşı, yön ve uzunluk korunarak bitiş de hareket etsin
                    this.baslangicX = roundedX;
                    this.baslangicY = roundedY;
                    this.baslangicZ = roundedZ;
                    hedefCizgi.baslangic = new THREE.Vector3(roundedX, roundedY, roundedZ);
                    // Yön ve uzunluk korunarak bitiş noktasını hesapla
                    const yon = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                    const yeniBitis = hedefCizgi.baslangic.clone().add(yon.multiplyScalar(this.uzunluk));
                    hedefCizgi.bitis = yeniBitis;
                    this.bitisX = Math.round(yeniBitis.x * 100) / 100;
                    this.bitisY = Math.round(yeniBitis.y * 100) / 100;
                    this.bitisZ = Math.round(yeniBitis.z * 100) / 100;
                } else if (hedef === 'bitis') {
                    // Bitiş noktasını taşı, yön sabit kalacak şekilde uzunluk değişir
                    const baslangic = hedefCizgi.baslangic;
                    const yeniUzunluk = baslangic.distanceTo(yeniPozisyon);
                    this.uzunluk = Math.round(yeniUzunluk * 100) / 100;
                    // Yönü koru, sadece uzunluğu değiştir
                    const yon = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                    const yeniBitis = baslangic.clone().add(yon.multiplyScalar(this.uzunluk));
                    hedefCizgi.bitis = yeniBitis;
                    this.bitisX = Math.round(yeniBitis.x * 100) / 100;
                    this.bitisY = Math.round(yeniBitis.y * 100) / 100;
                    this.bitisZ = Math.round(yeniBitis.z * 100) / 100;
                }
                break;

            case 'merkezdenCift':
                if (hedef === 'orta') {
                    // Merkez noktasını taşı, uzunluk aynı kalsın
                    this.baslangicX = roundedX;
                    this.baslangicY = roundedY;
                    this.baslangicZ = roundedZ;
                    const yon = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                    const yarimUz = this.uzunluk / 2;
                    const yeniBaslangic = new THREE.Vector3(roundedX, roundedY, roundedZ).clone().sub(yon.clone().multiplyScalar(yarimUz));
                    const yeniBitis = new THREE.Vector3(roundedX, roundedY, roundedZ).clone().add(yon.clone().multiplyScalar(yarimUz));
                    hedefCizgi.baslangic = yeniBaslangic;
                    hedefCizgi.bitis = yeniBitis;
                } else if (hedef === 'baslangic' || hedef === 'bitis') {
                    // Uç noktayı taşı, merkez sabit kalacak şekilde uzunluk değişir
                    const merkez = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                    const yeniYarimUzunluk = merkez.distanceTo(yeniPozisyon);
                    this.uzunluk = Math.round(yeniYarimUzunluk * 2 * 100) / 100;
                    // Yeni uç noktaları hesapla
                    const yon = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                    const yeniBaslangic = merkez.clone().sub(yon.clone().multiplyScalar(yeniYarimUzunluk));
                    const yeniBitis = merkez.clone().add(yon.clone().multiplyScalar(yeniYarimUzunluk));
                    hedefCizgi.baslangic = yeniBaslangic;
                    hedefCizgi.bitis = yeniBitis;
                }
                break;

            case 'yatay':
                // Sadece X ekseni değişebilir
                if (hedef === 'baslangic') {
                    this.baslangicX = roundedX;
                    hedefCizgi.baslangic = new THREE.Vector3(roundedX, this.baslangicY, this.baslangicZ);
                } else if (hedef === 'bitis') {
                    this.bitisX = roundedX;
                    hedefCizgi.bitis = new THREE.Vector3(roundedX, this.baslangicY, this.baslangicZ);
                }
                break;

            case 'dikey':
                // Sadece Y ekseni değişebilir
                if (hedef === 'baslangic') {
                    this.baslangicY = roundedY;
                    hedefCizgi.baslangic = new THREE.Vector3(this.baslangicX, roundedY, this.baslangicZ);
                } else if (hedef === 'bitis') {
                    this.bitisY = roundedY;
                    hedefCizgi.bitis = new THREE.Vector3(this.baslangicX, roundedY, this.baslangicZ);
                }
                break;

            case 'derinlik':
                // Sadece Z ekseni değişebilir
                if (hedef === 'baslangic') {
                    this.baslangicZ = roundedZ;
                    hedefCizgi.baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, roundedZ);
                } else if (hedef === 'bitis') {
                    this.bitisZ = roundedZ;
                    hedefCizgi.bitis = new THREE.Vector3(this.baslangicX, this.baslangicY, roundedZ);
                }
                break;

            case 'noktadanAciUzunluk':
                if (hedef === 'baslangic') {
                    // Başlangıç serbest, açı ve uzunluk korunur
                    this.baslangicX = roundedX;
                    this.baslangicY = roundedY;
                    this.baslangicZ = roundedZ;
                    hedefCizgi.baslangic = new THREE.Vector3(roundedX, roundedY, roundedZ);
                    const aciRad = this.aciDerece * Math.PI / 180;
                    const yeniBitis = new THREE.Vector3(
                        roundedX + this.uzunluk * Math.cos(aciRad),
                        roundedY + this.uzunluk * Math.sin(aciRad),
                        roundedZ
                    );
                    hedefCizgi.bitis = yeniBitis;
                    this.bitisX = Math.round(yeniBitis.x * 100) / 100;
                    this.bitisY = Math.round(yeniBitis.y * 100) / 100;
                    this.bitisZ = Math.round(yeniBitis.z * 100) / 100;
                } else if (hedef === 'bitis') {
                    // Bitiş: açı sabit, uzunluk değişir
                    const baslangicPos = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                    const aciRad2 = this.aciDerece * Math.PI / 180;
                    const yonVec = new THREE.Vector3(Math.cos(aciRad2), Math.sin(aciRad2), 0);
                    const delta = yeniPozisyon.clone().sub(baslangicPos);
                    const projeksiyon = Math.max(1, delta.dot(yonVec));
                    this.uzunluk = Math.round(projeksiyon * 100) / 100;
                    const yeniBitis2 = baslangicPos.clone().add(yonVec.multiplyScalar(this.uzunluk));
                    hedefCizgi.bitis = yeniBitis2;
                    this.bitisX = Math.round(yeniBitis2.x * 100) / 100;
                    this.bitisY = Math.round(yeniBitis2.y * 100) / 100;
                    this.bitisZ = Math.round(yeniBitis2.z * 100) / 100;
                }
                break;

            case 'noktadan3DAci':
                if (hedef === 'baslangic') {
                    // Başlangıç serbest, açılar ve uzunluk korunur
                    this.baslangicX = roundedX;
                    this.baslangicY = roundedY;
                    this.baslangicZ = roundedZ;
                    hedefCizgi.baslangic = new THREE.Vector3(roundedX, roundedY, roundedZ);
                    const thetaRad = this.thetaDerece * Math.PI / 180;
                    const phiRad = this.phiDerece * Math.PI / 180;
                    const yeniBitis3D = new THREE.Vector3(
                        roundedX + this.uzunluk * Math.sin(phiRad) * Math.cos(thetaRad),
                        roundedY + this.uzunluk * Math.sin(phiRad) * Math.sin(thetaRad),
                        roundedZ + this.uzunluk * Math.cos(phiRad)
                    );
                    hedefCizgi.bitis = yeniBitis3D;
                    this.bitisX = Math.round(yeniBitis3D.x * 100) / 100;
                    this.bitisY = Math.round(yeniBitis3D.y * 100) / 100;
                    this.bitisZ = Math.round(yeniBitis3D.z * 100) / 100;
                } else if (hedef === 'bitis') {
                    // Bitiş: açılar sabit, uzunluk değişir
                    const baslangic3D = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                    const thetaRad2 = this.thetaDerece * Math.PI / 180;
                    const phiRad2 = this.phiDerece * Math.PI / 180;
                    const yon3D = new THREE.Vector3(
                        Math.sin(phiRad2) * Math.cos(thetaRad2),
                        Math.sin(phiRad2) * Math.sin(thetaRad2),
                        Math.cos(phiRad2)
                    );
                    const delta3D = yeniPozisyon.clone().sub(baslangic3D);
                    const projeksiyon3D = Math.max(1, delta3D.dot(yon3D));
                    this.uzunluk = Math.round(projeksiyon3D * 100) / 100;
                    const yeniBitis3DA = baslangic3D.clone().add(yon3D.multiplyScalar(this.uzunluk));
                    hedefCizgi.bitis = yeniBitis3DA;
                    this.bitisX = Math.round(yeniBitis3DA.x * 100) / 100;
                    this.bitisY = Math.round(yeniBitis3DA.y * 100) / 100;
                    this.bitisZ = Math.round(yeniBitis3DA.z * 100) / 100;
                }
                break;

            default:
                // Diğer yöntemler için serbest taşıma
                if (hedef === 'baslangic') {
                    this.baslangicX = roundedX;
                    this.baslangicY = roundedY;
                    this.baslangicZ = roundedZ;
                    hedefCizgi.baslangic = new THREE.Vector3(roundedX, roundedY, roundedZ);
                } else if (hedef === 'bitis') {
                    this.bitisX = roundedX;
                    this.bitisY = roundedY;
                    this.bitisZ = roundedZ;
                    hedefCizgi.bitis = new THREE.Vector3(roundedX, roundedY, roundedZ);
                }
                // Uzunluğu güncelle
                this.uzunluk = Math.round(hedefCizgi.uzunluk * 100) / 100;
        }

        // Handle pozisyonlarını güncelle
        this.handles.guncelle();

        // Change detection tetikle
        this.cd.detectChanges();
    }

    // ========================================================================
    // UI'DAN ÇİZGİ GÜNCELLEME (Anlık yansıma)
    // ========================================================================

    /**
     * UI'daki input değişikliklerinde çizgiyi anlık güncelle
     * Input'lardaki (ngModelChange) event'i ile çağrılır
     */
    cizgiyiAnlikGuncelle(): void {
        // Geçici çizgi varsa onu güncelle (yeni çizgi modu)
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            this.geciciCizgiGuncelle();
            return;
        }

        // Düzenleme modunda mevcut çizgiyi güncelle
        if (!this.duzenleModu || !this.mevcutCizgi) return;

        // Seçilen yönteme göre başlangıç ve bitiş noktalarını hesapla
        let baslangic: THREE.Vector3;
        let bitis: THREE.Vector3;

        switch (this.seciliYontem) {
            case 'noktadanNoktaya':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.bitisY, this.bitisZ);
                break;

            case 'noktadanYonUzunluk':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const yon = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                bitis = baslangic.clone().add(yon.multiplyScalar(this.uzunluk));
                // Bitiş koordinatlarını da güncelle (UI senkronizasyonu)
                this.bitisX = Math.round(bitis.x * 100) / 100;
                this.bitisY = Math.round(bitis.y * 100) / 100;
                this.bitisZ = Math.round(bitis.z * 100) / 100;
                break;

            case 'noktadanAciUzunluk':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const aciRad = this.aciDerece * Math.PI / 180;
                bitis = new THREE.Vector3(
                    baslangic.x + Math.cos(aciRad) * this.uzunluk,
                    baslangic.y + Math.sin(aciRad) * this.uzunluk,
                    baslangic.z
                );
                break;

            case 'noktadan3DAci':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const theta = this.thetaDerece * Math.PI / 180;
                const phi = this.phiDerece * Math.PI / 180;
                bitis = new THREE.Vector3(
                    baslangic.x + this.uzunluk * Math.sin(phi) * Math.cos(theta),
                    baslangic.y + this.uzunluk * Math.cos(phi),
                    baslangic.z + this.uzunluk * Math.sin(phi) * Math.sin(theta)
                );
                break;

            case 'yatay':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.baslangicY, this.baslangicZ);
                break;

            case 'dikey':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.baslangicX, this.bitisY, this.baslangicZ);
                break;

            case 'derinlik':
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.baslangicX, this.baslangicY, this.bitisZ);
                break;

            case 'merkezdenCift':
                const merkez = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                const yonCift = new THREE.Vector3(this.yonX, this.yonY, this.yonZ).normalize();
                const yarimUzunluk = this.uzunluk / 2;
                baslangic = merkez.clone().sub(yonCift.clone().multiplyScalar(yarimUzunluk));
                bitis = merkez.clone().add(yonCift.clone().multiplyScalar(yarimUzunluk));
                break;

            default:
                baslangic = new THREE.Vector3(this.baslangicX, this.baslangicY, this.baslangicZ);
                bitis = new THREE.Vector3(this.bitisX, this.bitisY, this.bitisZ);
        }

        // Çizgi pozisyonlarını güncelle
        this.mevcutCizgi.baslangic = baslangic;
        this.mevcutCizgi.bitis = bitis;

        // Handle pozisyonlarını güncelle
        if (this.handles) {
            this.handles.guncelle();
        }

        // Uzunluğu güncelle (bazı yöntemler için)
        if (this.seciliYontem === 'noktadanNoktaya') {
            this.uzunluk = Math.round(this.mevcutCizgi.uzunluk * 100) / 100;
        }
    }

    /**
     * Renk değişikliğinde çizgiyi anlık güncelle
     */
    renkDegisti(): void {
        // Geçici çizgi varsa onu güncelle
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            this.geciciCizgi.renk = this.renk;
            return;
        }
        // Düzenleme modunda mevcut çizgiyi güncelle
        if (!this.duzenleModu || !this.mevcutCizgi) return;
        this.mevcutCizgi.renk = this.renk;
    }

    /**
     * Kalınlık değişikliğinde çizgiyi anlık güncelle
     */
    kalinlikDegisti(): void {
        // Geçici çizgi varsa onu güncelle
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            this.geciciCizgi.kalinlik = this.kalinlik;
            return;
        }
        // Düzenleme modunda mevcut çizgiyi güncelle
        if (!this.duzenleModu || !this.mevcutCizgi) return;
        this.mevcutCizgi.kalinlik = this.kalinlik;
    }

    /**
     * Opaklık değişikliğinde çizgiyi anlık güncelle
     */
    opaklikDegisti(): void {
        // Geçici çizgi varsa onu güncelle
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            this.geciciCizgi.opaklık = this.opaklik;
            return;
        }
        // Düzenleme modunda mevcut çizgiyi güncelle
        if (!this.duzenleModu || !this.mevcutCizgi) return;
        this.mevcutCizgi.opaklık = this.opaklik;
    }

    /**
     * Stil değişikliğinde çizgiyi anlık güncelle
     */
    stilDegisti(): void {
        // Geçici çizgi varsa onu güncelle
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            this.geciciCizgi.stil = this.stil;
            return;
        }
        // Düzenleme modunda mevcut çizgiyi güncelle
        if (!this.duzenleModu || !this.mevcutCizgi) return;
        this.mevcutCizgi.stil = this.stil;
    }

    /**
     * En üstte değişikliğinde çizgiyi anlık güncelle
     * depthTest ve depthWrite'ı kapatarak çizginin her zaman üstte görünmesini sağlar
     */
    enUsteDegisti(): void {
        // Geçici çizgi varsa onu güncelle
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            this.geciciCizgi.enUstte = this.enUstte;
            return;
        }
        // Düzenleme modunda mevcut çizgiyi güncelle
        if (!this.duzenleModu || !this.mevcutCizgi) return;
        this.mevcutCizgi.enUstte = this.enUstte;
    }

    /**
     * Render et değişikliğinde çizgiyi anlık güncelle
     */
    renderEtDegisti(): void {
        // Geçici çizgi varsa onu güncelle
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            this.geciciCizgi.renderEt = this.renderEt;
            return;
        }
        // Düzenleme modunda mevcut çizgiyi güncelle
        if (!this.duzenleModu || !this.mevcutCizgi) return;
        this.mevcutCizgi.renderEt = this.renderEt;
    }

    /**
     * Seçilebilir değişikliğinde çizgiyi anlık güncelle
     */
    secilebilirDegisti(): void {
        // Geçici çizgi varsa onu güncelle
        if (this.geciciCizgiAktif && this.geciciCizgi) {
            this.geciciCizgi.secilebilir = this.secilebilir;
            return;
        }
        // Düzenleme modunda mevcut çizgiyi güncelle
        if (!this.duzenleModu || !this.mevcutCizgi) return;
        this.mevcutCizgi.secilebilir = this.secilebilir;
    }
}
